// The App Share sheet's Public link section (src/apps/public).
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  title: 'Public link',
  help: 'Anyone with the link can open a read-only copy of this App, without signing in.',
  whatTitle: 'What becomes public',
  pages: 'This saved version’s pages and design',
  copyOf: 'A copy of {table}: {fields}',
  copyFiltered: 'A copy of the {table} rows its filter matches: {fields}',
  sends: 'Visitors can send {action} to {table}',
  sendsRuns: 'Each submission starts {functions}.',
  sendsRunsOff: '{functions} would also start once turned on.',
  never: 'Never public: the App’s other tables, your connections, secrets and server functions.',
  nothingDeclared:
    'The App shows no saved data publicly yet. To choose what visitors see or send, ask in the App’s chat.',
  mark: 'The page shows “Made with Poneva” in a corner.',
  confirm: 'I understand that anyone with the link can see this.',
  publish: 'Publish to a public link',
  publishing: 'Publishing…',
  republish: 'Publish this version',
  behind: 'The public link shows an earlier version. Publish this one to update it.',
  live: 'Live',
  copyLink: 'Copy link',
  copied: 'Copied',
  open: 'Open',
  copiedAt: 'Data copied {time}',
  refresh: 'Refresh data',
  refreshing: 'Refreshing…',
  embedTitle: 'Embed on your website',
  embedHelp:
    'List the sites that may show this App, such as https://shop.example.com, then paste the code into a page there.',
  embedSites: 'Sites that may embed it',
  saveSites: 'Save sites',
  saving: 'Saving…',
  copyCode: 'Copy code',
  noSites: 'Add a site first: the App can only be embedded on sites you list.',
  unpublish: 'Unpublish',
  unpublishBody: 'The link stops working at once and its copy is deleted.',
  unpublishConfirm: 'Unpublish now',
  unpublishing: 'Unpublishing…',
  cancel: 'Cancel',
  takenDown: 'Poneva took this public link down. Contact support to restore it.',
  takenDownReason: 'Reason: {reason}',
  viewerLive: 'Anyone with this link can open a read-only copy.',
  adminLive:
    'Anyone with this link can open a read-only copy. As a company admin, you can take it down.',
  failed: 'We couldn’t change the public link. Try again.',
  sitesInvalid: 'Enter each site as https:// and its address, without a path.',
  copyTooLarge: 'A table is too large to copy publicly. Narrow it with a filter in the App’s chat.',
  rebuild: 'Save the App again in its chat, then publish it.',
  loadFailed: 'The public link could not be read.',
  retry: 'Try again',
  checking:
    'We’re checking this version for leaked keys and unsafe packages. Try again in a moment.',
  checkFailed: 'The safety check couldn’t finish. Try again in a moment.',
  checkBlockedSecret:
    'This version contains what looks like a secret key, so it can’t be made public. Remove it in the App’s chat and save again.',
  checkBlockedDependency:
    'This version uses a package with a known security problem, so it can’t be made public. Update it in the App’s chat and save again.',
  versionChanged: 'A newer version was saved. Open it and publish again.',
  embedSitesPlaceholder: 'https://shop.example.com',
};

const copy: NamespaceCatalog<keyof typeof en> = {
  bg: {
    title: 'Публична връзка',
    help: 'Всеки с връзката може да отвори копие на това приложение само за четене, без да влиза в профил.',
    whatTitle: 'Какво става публично',
    pages: 'Страниците и дизайнът на тази запазена версия',
    copyOf: 'Копие на {table}: {fields}',
    copyFiltered: 'Копие на редовете от {table}, които отговарят на филтъра: {fields}',
    sends: 'Посетителите могат да изпращат {action} към {table}',
    sendsRuns: 'Всяко изпращане стартира {functions}.',
    sendsRunsOff: 'След включване ще се стартира и: {functions}.',
    never:
      'Никога не са публични: другите таблици на приложението, вашите връзки, тайни и сървърни функции.',
    nothingDeclared:
      'Приложението още не показва публично запазени данни. За да изберете какво виждат или изпращат посетителите, попитайте в чата на приложението.',
    mark: 'Страницата показва „Made with Poneva“ в ъгъла.',
    confirm: 'Разбирам, че всеки с връзката може да види това.',
    publish: 'Публикуване с публична връзка',
    publishing: 'Публикуване…',
    republish: 'Публикуване на тази версия',
    behind: 'Публичната връзка показва по-ранна версия. Публикувайте тази, за да я обновите.',
    live: 'На живо',
    copyLink: 'Копиране на връзката',
    copied: 'Копирано',
    open: 'Отваряне',
    copiedAt: 'Данните са копирани {time}',
    refresh: 'Обновяване на данните',
    refreshing: 'Обновяване…',
    embedTitle: 'Вграждане във вашия уебсайт',
    embedHelp:
      'Изброете сайтовете, които могат да показват това приложение, например https://shop.example.com, след което поставете кода в страница там.',
    embedSites: 'Сайтове, които могат да го вграждат',
    saveSites: 'Запазване на сайтовете',
    saving: 'Запазване…',
    copyCode: 'Копиране на кода',
    noSites: 'Първо добавете сайт: приложението може да се вгражда само в сайтове от вашия списък.',
    unpublish: 'Отмяна на публикуването',
    unpublishBody: 'Връзката спира да работи веднага и копието ѝ се изтрива.',
    unpublishConfirm: 'Отмяна сега',
    unpublishing: 'Отмяна на публикуването…',
    cancel: 'Отказ',
    takenDown:
      'Poneva свали тази публична връзка. Свържете се с поддръжката, за да я възстановите.',
    takenDownReason: 'Причина: {reason}',
    viewerLive: 'Всеки с тази връзка може да отвори копие само за четене.',
    adminLive:
      'Всеки с тази връзка може да отвори копие само за четене. Като администратор на компанията можете да я свалите.',
    failed: 'Не успяхме да променим публичната връзка. Опитайте отново.',
    sitesInvalid: 'Въведете всеки сайт като https:// и адреса му, без път.',
    copyTooLarge:
      'Таблица е твърде голяма, за да бъде копирана публично. Стеснете я с филтър в чата на приложението.',
    rebuild: 'Запазете приложението отново в чата му, после го публикувайте.',
    loadFailed: 'Публичната връзка не можа да бъде прочетена.',
    retry: 'Опитайте отново',
    checking:
      'Проверяваме тази версия за изтекли ключове и опасни пакети. Опитайте отново след малко.',
    checkFailed: 'Проверката за сигурност не можа да приключи. Опитайте отново след малко.',
    checkBlockedSecret:
      'Тази версия съдържа нещо, което прилича на таен ключ, затова не може да стане публична. Премахнете го в чата на приложението и запазете отново.',
    checkBlockedDependency:
      'Тази версия използва пакет с известен проблем със сигурността, затова не може да стане публична. Обновете го в чата на приложението и запазете отново.',
    versionChanged: 'Запазена е по-нова версия. Отворете я и публикувайте отново.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  hr: {
    title: 'Javna poveznica',
    help: 'Svatko s poveznicom može otvoriti kopiju ove aplikacije samo za čitanje, bez prijave.',
    whatTitle: 'Što postaje javno',
    pages: 'Stranice i dizajn ove spremljene verzije',
    copyOf: 'Kopija tablice {table}: {fields}',
    copyFiltered: 'Kopija redaka tablice {table} koji odgovaraju njezinu filtru: {fields}',
    sends: 'Posjetitelji mogu slati {action} u tablicu {table}',
    sendsRuns: 'Svako slanje pokreće: {functions}.',
    sendsRunsOff: 'Nakon uključivanja pokrenut će se i: {functions}.',
    never:
      'Nikad javno: ostale tablice aplikacije, vaše veze, tajni ključevi i poslužiteljske funkcije.',
    nothingDeclared:
      'Aplikacija još javno ne prikazuje nikakve spremljene podatke. Da biste odabrali što posjetitelji vide ili šalju, pitajte u chatu aplikacije.',
    mark: 'Stranica u kutu prikazuje „Izrađeno u Poneva“.',
    confirm: 'Razumijem da ovo može vidjeti svatko s poveznicom.',
    publish: 'Objavi na javnoj poveznici',
    publishing: 'Objavljivanje…',
    republish: 'Objavi ovu verziju',
    behind: 'Javna poveznica prikazuje raniju verziju. Objavite ovu da biste je ažurirali.',
    live: 'Objavljeno',
    copyLink: 'Kopiraj poveznicu',
    copied: 'Kopirano',
    open: 'Otvori',
    copiedAt: 'Podaci kopirani {time}',
    refresh: 'Osvježi podatke',
    refreshing: 'Osvježavanje…',
    embedTitle: 'Ugradi na svoju web-stranicu',
    embedHelp:
      'Navedite web-stranice koje smiju prikazivati ovu aplikaciju, na primjer https://shop.example.com, a zatim zalijepite kôd na stranicu na jednoj od njih.',
    embedSites: 'Web-stranice koje je smiju ugraditi',
    saveSites: 'Spremi web-stranice',
    saving: 'Spremanje…',
    copyCode: 'Kopiraj kôd',
    noSites:
      'Najprije dodajte web-stranicu: aplikaciju je moguće ugraditi samo na navedene web-stranice.',
    unpublish: 'Povuci objavu',
    unpublishBody: 'Poveznica odmah prestaje raditi, a njezina se kopija briše.',
    unpublishConfirm: 'Povuci objavu sada',
    unpublishing: 'Povlačenje objave…',
    cancel: 'Odustani',
    takenDown: 'Poneva je uklonila ovu javnu poveznicu. Obratite se podršci za vraćanje.',
    takenDownReason: 'Razlog: {reason}',
    viewerLive: 'Svatko s ovom poveznicom može otvoriti kopiju samo za čitanje.',
    adminLive:
      'Svatko s ovom poveznicom može otvoriti kopiju samo za čitanje. Kao administrator tvrtke možete je ukloniti.',
    failed: 'Javnu poveznicu nije bilo moguće promijeniti. Pokušajte ponovno.',
    sitesInvalid: 'Unesite svaku web-stranicu kao https:// i njezinu adresu, bez putanje.',
    copyTooLarge: 'Tablica je prevelika za javno kopiranje. Suzite je filtrom u chatu aplikacije.',
    rebuild: 'Ponovno spremite aplikaciju u njezinu chatu, a zatim je objavite.',
    loadFailed: 'Javnu poveznicu nije bilo moguće pročitati.',
    retry: 'Pokušaj ponovno',
    checking:
      'Provjeravamo sadrži li ova verzija procurjele ključeve i nesigurne pakete. Pokušajte ponovno za trenutak.',
    checkFailed: 'Sigurnosnu provjeru nije bilo moguće dovršiti. Pokušajte ponovno za trenutak.',
    checkBlockedSecret:
      'Ova verzija sadrži nešto što izgleda kao tajni ključ, pa se ne može objaviti javno. Uklonite ga u chatu aplikacije i ponovno spremite.',
    checkBlockedDependency:
      'Ova verzija koristi paket s poznatim sigurnosnim problemom, pa se ne može objaviti javno. Ažurirajte ga u chatu aplikacije i ponovno spremite.',
    versionChanged: 'Spremljena je novija verzija. Otvorite je i ponovno objavite.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  cs: {
    title: 'Veřejný odkaz',
    help: 'Kdokoli s odkazem může otevřít kopii této aplikace jen pro čtení, bez přihlášení.',
    whatTitle: 'Co se stane veřejným',
    pages: 'Stránky a návrh této uložené verze',
    copyOf: 'Kopie tabulky {table}: {fields}',
    copyFiltered: 'Kopie řádků tabulky {table}, které odpovídají jejímu filtru: {fields}',
    sends: 'Návštěvníci mohou odesílat {action} do tabulky {table}',
    sendsRuns: 'Každé odeslání spustí: {functions}.',
    sendsRunsOff: 'Po zapnutí se spustí také: {functions}.',
    never:
      'Nikdy veřejné: ostatní tabulky aplikace, vaše připojení, tajné klíče a serverové funkce.',
    nothingDeclared:
      'Aplikace zatím veřejně nezobrazuje žádná uložená data. Chcete-li zvolit, co návštěvníci uvidí nebo odešlou, požádejte o to v chatu aplikace.',
    mark: 'Stránka v rohu zobrazuje „Vytvořeno v Poneva“.',
    confirm: 'Rozumím, že to může vidět kdokoli s odkazem.',
    publish: 'Publikovat na veřejný odkaz',
    publishing: 'Publikování…',
    republish: 'Publikovat tuto verzi',
    behind: 'Veřejný odkaz zobrazuje dřívější verzi. Publikujte tuto, abyste ho aktualizovali.',
    live: 'Publikováno',
    copyLink: 'Kopírovat odkaz',
    copied: 'Zkopírováno',
    open: 'Otevřít',
    copiedAt: 'Data zkopírována {time}',
    refresh: 'Obnovit data',
    refreshing: 'Obnovování…',
    embedTitle: 'Vložit na váš web',
    embedHelp:
      'Uveďte weby, které smějí tuto aplikaci zobrazovat, například https://shop.example.com, a poté vložte kód na stránku na některém z nich.',
    embedSites: 'Weby, které ji smějí vložit',
    saveSites: 'Uložit weby',
    saving: 'Ukládání…',
    copyCode: 'Kopírovat kód',
    noSites: 'Nejprve přidejte web: aplikaci lze vložit jen na weby, které uvedete.',
    unpublish: 'Zrušit publikování',
    unpublishBody: 'Odkaz okamžitě přestane fungovat a jeho kopie se smaže.',
    unpublishConfirm: 'Zrušit publikování',
    unpublishing: 'Ruším publikování…',
    cancel: 'Zrušit',
    takenDown: 'Poneva tento veřejný odkaz odstranila. Pro jeho obnovení kontaktujte podporu.',
    takenDownReason: 'Důvod: {reason}',
    viewerLive: 'Kdokoli s tímto odkazem může otevřít kopii jen pro čtení.',
    adminLive:
      'Kdokoli s tímto odkazem může otevřít kopii jen pro čtení. Jako správce firmy ho můžete odstranit.',
    failed: 'Veřejný odkaz se nepodařilo změnit. Zkuste to znovu.',
    sitesInvalid: 'Zadejte každý web jako https:// a jeho adresu, bez cesty.',
    copyTooLarge:
      'Tabulka je příliš velká na veřejné kopírování. Zúžte ji filtrem v chatu aplikace.',
    rebuild: 'Uložte aplikaci znovu v jejím chatu a poté ji publikujte.',
    loadFailed: 'Veřejný odkaz se nepodařilo přečíst.',
    retry: 'Zkusit znovu',
    checking:
      'Kontrolujeme, zda tato verze neobsahuje uniklé klíče a nebezpečné balíčky. Zkuste to za chvíli znovu.',
    checkFailed: 'Bezpečnostní kontrolu se nepodařilo dokončit. Zkuste to za chvíli znovu.',
    checkBlockedSecret:
      'Tato verze obsahuje něco, co vypadá jako tajný klíč, proto ji nelze zveřejnit. Odstraňte ho v chatu aplikace a uložte znovu.',
    checkBlockedDependency:
      'Tato verze používá balíček se známým bezpečnostním problémem, proto ji nelze zveřejnit. Aktualizujte ho v chatu aplikace a uložte znovu.',
    versionChanged: 'Byla uložena novější verze. Otevřete ji a publikujte znovu.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  da: {
    title: 'Offentligt link',
    help: 'Alle med linket kan åbne en skrivebeskyttet kopi af denne app uden at logge ind.',
    whatTitle: 'Hvad der bliver offentligt',
    pages: 'Denne gemte versions sider og design',
    copyOf: 'En kopi af {table}: {fields}',
    copyFiltered: 'En kopi af de rækker i {table}, som filteret matcher: {fields}',
    sends: 'Besøgende kan sende {action} til {table}',
    sendsRuns: 'Hver indsendelse starter {functions}.',
    sendsRunsOff: '{functions} ville også starte, når det slås til.',
    never:
      'Aldrig offentligt: appens andre tabeller, dine forbindelser, hemmeligheder og serverfunktioner.',
    nothingDeclared:
      'Appen viser endnu ingen gemte data offentligt. Spørg i appens chat for at vælge, hvad besøgende ser eller sender.',
    mark: 'Siden viser “Made with Poneva” i et hjørne.',
    confirm: 'Jeg forstår, at alle med linket kan se dette.',
    publish: 'Udgiv på et offentligt link',
    publishing: 'Udgiver…',
    republish: 'Udgiv denne version',
    behind: 'Det offentlige link viser en tidligere version. Udgiv denne for at opdatere det.',
    live: 'Live',
    copyLink: 'Kopiér link',
    copied: 'Kopieret',
    open: 'Åbn',
    copiedAt: 'Data kopieret {time}',
    refresh: 'Opdater data',
    refreshing: 'Opdaterer…',
    embedTitle: 'Indlejr på din hjemmeside',
    embedHelp:
      'Angiv de sider, der må vise denne app, f.eks. https://shop.example.com, og indsæt derefter koden på en side dér.',
    embedSites: 'Sider, der må indlejre den',
    saveSites: 'Gem sider',
    saving: 'Gemmer…',
    copyCode: 'Kopiér kode',
    noSites: 'Tilføj først en side: Appen kan kun indlejres på sider, du angiver.',
    unpublish: 'Træk tilbage',
    unpublishBody: 'Linket holder op med at virke med det samme, og kopien slettes.',
    unpublishConfirm: 'Træk tilbage nu',
    unpublishing: 'Trækker tilbage…',
    cancel: 'Annuller',
    takenDown: 'Poneva har taget dette offentlige link ned. Kontakt support for at genoprette det.',
    takenDownReason: 'Årsag: {reason}',
    viewerLive: 'Alle med dette link kan åbne en skrivebeskyttet kopi.',
    adminLive:
      'Alle med dette link kan åbne en skrivebeskyttet kopi. Som virksomhedens admin kan du tage det ned.',
    failed: 'Vi kunne ikke ændre det offentlige link. Prøv igen.',
    sitesInvalid: 'Angiv hver side som https:// og dens adresse, uden sti.',
    copyTooLarge:
      'En tabel er for stor til en offentlig kopi. Begræns den med et filter i appens chat.',
    rebuild: 'Gem appen igen i dens chat, og udgiv den derefter.',
    loadFailed: 'Det offentlige link kunne ikke læses.',
    retry: 'Prøv igen',
    checking:
      'Vi tjekker denne version for lækkede nøgler og usikre pakker. Prøv igen om et øjeblik.',
    checkFailed: 'Sikkerhedstjekket kunne ikke gennemføres. Prøv igen om et øjeblik.',
    checkBlockedSecret:
      'Denne version indeholder noget, der ligner en hemmelig nøgle, så den kan ikke gøres offentlig. Fjern den i appens chat, og gem igen.',
    checkBlockedDependency:
      'Denne version bruger en pakke med et kendt sikkerhedsproblem, så den kan ikke gøres offentlig. Opdater den i appens chat, og gem igen.',
    versionChanged: 'En nyere version blev gemt. Åbn den, og udgiv igen.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  nl: {
    title: 'Openbare link',
    help: 'Iedereen met de link kan zonder in te loggen een alleen-lezen kopie van deze app openen.',
    whatTitle: 'Wat openbaar wordt',
    pages: 'De pagina’s en het ontwerp van deze opgeslagen versie',
    copyOf: 'Een kopie van {table}: {fields}',
    copyFiltered: 'Een kopie van de rijen van {table} die bij het filter passen: {fields}',
    sends: 'Bezoekers kunnen {action} naar {table} sturen',
    sendsRuns: 'Elke inzending start {functions}.',
    sendsRunsOff: '{functions} zou ook starten zodra het is ingeschakeld.',
    never:
      'Nooit openbaar: de andere tabellen van de app, je verbindingen, geheimen en serverfuncties.',
    nothingDeclared:
      'De app toont nog geen opgeslagen gegevens openbaar. Vraag het in de chat van de app om te kiezen wat bezoekers zien of sturen.',
    mark: 'De pagina toont “Made with Poneva” in een hoek.',
    confirm: 'Ik begrijp dat iedereen met de link dit kan zien.',
    publish: 'Publiceren naar een openbare link',
    publishing: 'Publiceren…',
    republish: 'Deze versie publiceren',
    behind:
      'De openbare link toont een eerdere versie. Publiceer deze versie om hem bij te werken.',
    live: 'Live',
    copyLink: 'Link kopiëren',
    copied: 'Gekopieerd',
    open: 'Openen',
    copiedAt: 'Gegevens gekopieerd {time}',
    refresh: 'Gegevens vernieuwen',
    refreshing: 'Vernieuwen…',
    embedTitle: 'Insluiten op je website',
    embedHelp:
      'Noem de sites die deze app mogen tonen, zoals https://shop.example.com, en plak de code dan in een pagina daar.',
    embedSites: 'Sites die de app mogen insluiten',
    saveSites: 'Sites opslaan',
    saving: 'Opslaan…',
    copyCode: 'Code kopiëren',
    noSites:
      'Voeg eerst een site toe: de app kan alleen worden ingesloten op sites die je opgeeft.',
    unpublish: 'Publicatie ongedaan maken',
    unpublishBody: 'De link werkt meteen niet meer en de kopie wordt verwijderd.',
    unpublishConfirm: 'Nu ongedaan maken',
    unpublishing: 'Ongedaan maken…',
    cancel: 'Annuleren',
    takenDown:
      'Poneva heeft deze openbare link offline gehaald. Neem contact op met support om hem te herstellen.',
    takenDownReason: 'Reden: {reason}',
    viewerLive: 'Iedereen met deze link kan een alleen-lezen kopie openen.',
    adminLive:
      'Iedereen met deze link kan een alleen-lezen kopie openen. Als bedrijfsbeheerder kun je hem offline halen.',
    failed: 'We konden de openbare link niet wijzigen. Probeer het opnieuw.',
    sitesInvalid: 'Voer elke site in als https:// met het adres, zonder pad.',
    copyTooLarge:
      'Een tabel is te groot voor een openbare kopie. Beperk hem met een filter in de chat van de app.',
    rebuild: 'Sla de app opnieuw op in de chat en publiceer hem dan.',
    loadFailed: 'De openbare link kon niet worden gelezen.',
    retry: 'Opnieuw proberen',
    checking:
      'We controleren deze versie op uitgelekte sleutels en onveilige pakketten. Probeer het over een moment opnieuw.',
    checkFailed:
      'De veiligheidscontrole kon niet worden voltooid. Probeer het over een moment opnieuw.',
    checkBlockedSecret:
      'Deze versie bevat wat op een geheime sleutel lijkt en kan daarom niet openbaar worden gemaakt. Verwijder die in de chat van de app en sla opnieuw op.',
    checkBlockedDependency:
      'Deze versie gebruikt een pakket met een bekend beveiligingsprobleem en kan daarom niet openbaar worden gemaakt. Werk het bij in de chat van de app en sla opnieuw op.',
    versionChanged: 'Er is een nieuwere versie opgeslagen. Open die en publiceer opnieuw.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  en: {
    title: 'Public link',
    help: 'Anyone with the link can open a read-only copy of this App, without signing in.',
    whatTitle: 'What becomes public',
    pages: 'This saved version’s pages and design',
    copyOf: 'A copy of {table}: {fields}',
    copyFiltered: 'A copy of the {table} rows its filter matches: {fields}',
    sends: 'Visitors can send {action} to {table}',
    sendsRuns: 'Each submission starts {functions}.',
    sendsRunsOff: '{functions} would also start once turned on.',
    never: 'Never public: the App’s other tables, your connections, secrets and server functions.',
    nothingDeclared:
      'The App shows no saved data publicly yet. To choose what visitors see or send, ask in the App’s chat.',
    mark: 'The page shows “Made with Poneva” in a corner.',
    confirm: 'I understand that anyone with the link can see this.',
    publish: 'Publish to a public link',
    publishing: 'Publishing…',
    republish: 'Publish this version',
    behind: 'The public link shows an earlier version. Publish this one to update it.',
    live: 'Live',
    copyLink: 'Copy link',
    copied: 'Copied',
    open: 'Open',
    copiedAt: 'Data copied {time}',
    refresh: 'Refresh data',
    refreshing: 'Refreshing…',
    embedTitle: 'Embed on your website',
    embedHelp:
      'List the sites that may show this App, such as https://shop.example.com, then paste the code into a page there.',
    embedSites: 'Sites that may embed it',
    saveSites: 'Save sites',
    saving: 'Saving…',
    copyCode: 'Copy code',
    noSites: 'Add a site first: the App can only be embedded on sites you list.',
    unpublish: 'Unpublish',
    unpublishBody: 'The link stops working at once and its copy is deleted.',
    unpublishConfirm: 'Unpublish now',
    unpublishing: 'Unpublishing…',
    cancel: 'Cancel',
    takenDown: 'Poneva took this public link down. Contact support to restore it.',
    takenDownReason: 'Reason: {reason}',
    viewerLive: 'Anyone with this link can open a read-only copy.',
    adminLive:
      'Anyone with this link can open a read-only copy. As a company admin, you can take it down.',
    failed: 'We couldn’t change the public link. Try again.',
    sitesInvalid: 'Enter each site as https:// and its address, without a path.',
    copyTooLarge:
      'A table is too large to copy publicly. Narrow it with a filter in the App’s chat.',
    rebuild: 'Save the App again in its chat, then publish it.',
    loadFailed: 'The public link could not be read.',
    retry: 'Try again',
    checking:
      'We’re checking this version for leaked keys and unsafe packages. Try again in a moment.',
    checkFailed: 'The safety check couldn’t finish. Try again in a moment.',
    checkBlockedSecret:
      'This version contains what looks like a secret key, so it can’t be made public. Remove it in the App’s chat and save again.',
    checkBlockedDependency:
      'This version uses a package with a known security problem, so it can’t be made public. Update it in the App’s chat and save again.',
    versionChanged: 'A newer version was saved. Open it and publish again.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  et: {
    title: 'Avalik link',
    help: 'Igaüks, kellel on link, saab avada selle rakenduse kirjutuskaitstud koopia ilma sisse logimata.',
    whatTitle: 'Mis muutub avalikuks',
    pages: 'Selle salvestatud versiooni lehed ja kujundus',
    copyOf: 'Tabeli {table} koopia: {fields}',
    copyFiltered: 'Tabeli {table} filtrile vastavate ridade koopia: {fields}',
    sends: 'Külastajad saavad saata tabelisse {table} toimingu {action}',
    sendsRuns: 'Iga saatmine käivitab: {functions}.',
    sendsRunsOff: '{functions} käivituks samuti, kui see sisse lülitada.',
    never:
      'Mitte kunagi avalik: rakenduse muud tabelid, sinu ühendused, saladused ja serverifunktsioonid.',
    nothingDeclared:
      'Rakendus ei näita veel avalikult salvestatud andmeid. Küsi rakenduse vestluses, mida külastajad näevad või saadavad.',
    mark: 'Leht näitab nurgas teksti „Made with Poneva“.',
    confirm: 'Saan aru, et igaüks, kellel on link, näeb seda.',
    publish: 'Avalda avalikul lingil',
    publishing: 'Avaldamine…',
    republish: 'Avalda see versioon',
    behind: 'Avalik link näitab varasemat versiooni. Selle uuendamiseks avalda see versioon.',
    live: 'Avaldatud',
    copyLink: 'Kopeeri link',
    copied: 'Kopeeritud',
    open: 'Ava',
    copiedAt: 'Andmed kopeeritud {time}',
    refresh: 'Värskenda andmeid',
    refreshing: 'Värskendamine…',
    embedTitle: 'Manusta oma veebisaidile',
    embedHelp:
      'Loetle saidid, mis võivad seda rakendust näidata, näiteks https://shop.example.com, ja kleebi siis kood sealsele lehele.',
    embedSites: 'Saidid, mis võivad seda manustada',
    saveSites: 'Salvesta saidid',
    saving: 'Salvestamine…',
    copyCode: 'Kopeeri kood',
    noSites: 'Lisa kõigepealt sait: rakendust saab manustada ainult sinu loetletud saitidele.',
    unpublish: 'Võta avaldamine tagasi',
    unpublishBody: 'Link lakkab kohe töötamast ja selle koopia kustutatakse.',
    unpublishConfirm: 'Võta nüüd tagasi',
    unpublishing: 'Tagasivõtmine…',
    cancel: 'Tühista',
    takenDown: 'Poneva eemaldas selle avaliku lingi. Taastamiseks võta ühendust toega.',
    takenDownReason: 'Põhjus: {reason}',
    viewerLive: 'Igaüks, kellel on see link, saab avada kirjutuskaitstud koopia.',
    adminLive:
      'Igaüks, kellel on see link, saab avada kirjutuskaitstud koopia. Ettevõtte adminina saad selle eemaldada.',
    failed: 'Me ei saanud avalikku linki muuta. Proovi uuesti.',
    sitesInvalid: 'Sisesta iga sait kujul https:// ja selle aadress, ilma teeta.',
    copyTooLarge:
      'Tabel on avaliku koopia jaoks liiga suur. Kitsenda seda rakenduse vestluses filtriga.',
    rebuild: 'Salvesta rakendus selle vestluses uuesti ja avalda see siis.',
    loadFailed: 'Avalikku linki ei saanud lugeda.',
    retry: 'Proovi uuesti',
    checking:
      'Kontrollime seda versiooni lekkinud võtmete ja ebaturvaliste pakettide suhtes. Proovi hetke pärast uuesti.',
    checkFailed: 'Ohutuskontrolli ei saanud lõpetada. Proovi hetke pärast uuesti.',
    checkBlockedSecret:
      'See versioon sisaldab midagi, mis näeb välja nagu salavõti, seega ei saa seda avalikuks teha. Eemalda see rakenduse vestluses ja salvesta uuesti.',
    checkBlockedDependency:
      'See versioon kasutab paketti, millel on teadaolev turvaprobleem, seega ei saa seda avalikuks teha. Uuenda seda rakenduse vestluses ja salvesta uuesti.',
    versionChanged: 'Salvestati uuem versioon. Ava see ja avalda uuesti.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  fi: {
    title: 'Julkinen linkki',
    help: 'Kuka tahansa linkin saanut voi avata tämän sovelluksen vain luku -kopion kirjautumatta.',
    whatTitle: 'Mikä tulee julkiseksi',
    pages: 'Tämän tallennetun version sivut ja ulkoasu',
    copyOf: 'Kopio taulukosta {table}: {fields}',
    copyFiltered: 'Kopio taulukon {table} riveistä, jotka suodatin hyväksyy: {fields}',
    sends: 'Kävijät voivat lähettää toiminnon {action} taulukkoon {table}',
    sendsRuns: 'Jokainen lähetys käynnistää: {functions}.',
    sendsRunsOff: '{functions} käynnistyisi myös, kun se otetaan käyttöön.',
    never:
      'Ei koskaan julkista: sovelluksen muut taulukot, yhteytesi, salaisuudet ja palvelinfunktiot.',
    nothingDeclared:
      'Sovellus ei vielä näytä tallennettuja tietoja julkisesti. Kysy sovelluksen keskustelussa, mitä kävijät näkevät tai lähettävät.',
    mark: 'Sivu näyttää nurkassa tekstin “Made with Poneva”.',
    confirm: 'Ymmärrän, että kuka tahansa linkin saanut voi nähdä tämän.',
    publish: 'Julkaise julkiseen linkkiin',
    publishing: 'Julkaistaan…',
    republish: 'Julkaise tämä versio',
    behind: 'Julkinen linkki näyttää aiemman version. Päivitä se julkaisemalla tämä.',
    live: 'Käytössä',
    copyLink: 'Kopioi linkki',
    copied: 'Kopioitu',
    open: 'Avaa',
    copiedAt: 'Tiedot kopioitu {time}',
    refresh: 'Päivitä tiedot',
    refreshing: 'Päivitetään…',
    embedTitle: 'Upota verkkosivustollesi',
    embedHelp:
      'Luettele sivustot, jotka saavat näyttää tämän sovelluksen, esimerkiksi https://shop.example.com, ja liitä sitten koodi niiden sivulle.',
    embedSites: 'Sivustot, jotka saavat upottaa sen',
    saveSites: 'Tallenna sivustot',
    saving: 'Tallennetaan…',
    copyCode: 'Kopioi koodi',
    noSites: 'Lisää ensin sivusto: sovelluksen voi upottaa vain luettelemillesi sivustoille.',
    unpublish: 'Poista julkaisu',
    unpublishBody: 'Linkki lakkaa toimimasta heti ja sen kopio poistetaan.',
    unpublishConfirm: 'Poista julkaisu nyt',
    unpublishing: 'Poistetaan julkaisua…',
    cancel: 'Peruuta',
    takenDown: 'Poneva poisti tämän julkisen linkin. Ota yhteyttä tukeen sen palauttamiseksi.',
    takenDownReason: 'Syy: {reason}',
    viewerLive: 'Kuka tahansa tämän linkin saanut voi avata vain luku -kopion.',
    adminLive:
      'Kuka tahansa tämän linkin saanut voi avata vain luku -kopion. Yrityksen ylläpitäjänä voit poistaa sen käytöstä.',
    failed: 'Julkista linkkiä ei voitu muuttaa. Yritä uudelleen.',
    sitesInvalid: 'Kirjoita jokainen sivusto muodossa https:// ja sen osoite, ilman polkua.',
    copyTooLarge:
      'Taulukko on liian suuri julkiseen kopioon. Rajaa sitä suodattimella sovelluksen keskustelussa.',
    rebuild: 'Tallenna sovellus uudelleen sen keskustelussa ja julkaise se sitten.',
    loadFailed: 'Julkista linkkiä ei voitu lukea.',
    retry: 'Yritä uudelleen',
    checking:
      'Tarkistamme tästä versiosta vuotaneet avaimet ja vaaralliset paketit. Yritä hetken kuluttua uudelleen.',
    checkFailed: 'Turvatarkistusta ei voitu suorittaa loppuun. Yritä hetken kuluttua uudelleen.',
    checkBlockedSecret:
      'Tämä versio sisältää jotain, mikä näyttää salaiselta avaimelta, joten sitä ei voi julkaista. Poista se sovelluksen keskustelussa ja tallenna uudelleen.',
    checkBlockedDependency:
      'Tämä versio käyttää pakettia, jolla on tunnettu tietoturvaongelma, joten sitä ei voi julkaista. Päivitä paketti sovelluksen keskustelussa ja tallenna uudelleen.',
    versionChanged: 'Uudempi versio tallennettiin. Avaa se ja julkaise uudelleen.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  fr: {
    title: 'Lien public',
    help: 'Toute personne disposant du lien peut ouvrir une copie en lecture seule de cette application, sans se connecter.',
    whatTitle: 'Ce qui devient public',
    pages: 'Les pages et le design de cette version enregistrée',
    copyOf: 'Une copie de {table} : {fields}',
    copyFiltered: 'Une copie des lignes de {table} qui correspondent à son filtre : {fields}',
    sends: 'Les visiteurs peuvent envoyer {action} à {table}',
    sendsRuns: 'Chaque envoi lance {functions}.',
    sendsRunsOff: '{functions} se lancerait aussi une fois activé.',
    never:
      'Jamais public : les autres tables de l’application, vos connexions, secrets et fonctions serveur.',
    nothingDeclared:
      'L’application n’affiche pas encore de données enregistrées publiquement. Pour choisir ce que les visiteurs voient ou envoient, demandez dans le chat de l’application.',
    mark: 'La page affiche « Made with Poneva » dans un coin.',
    confirm: 'Je comprends que toute personne disposant du lien peut le voir.',
    publish: 'Publier sur un lien public',
    publishing: 'Publication…',
    republish: 'Publier cette version',
    behind:
      'Le lien public affiche une version antérieure. Publiez celle-ci pour le mettre à jour.',
    live: 'En ligne',
    copyLink: 'Copier le lien',
    copied: 'Copié',
    open: 'Ouvrir',
    copiedAt: 'Données copiées {time}',
    refresh: 'Actualiser les données',
    refreshing: 'Actualisation…',
    embedTitle: 'Intégrer à votre site web',
    embedHelp:
      'Indiquez les sites autorisés à afficher cette application, par exemple https://shop.example.com, puis collez le code dans une page de ce site.',
    embedSites: 'Sites autorisés à l’intégrer',
    saveSites: 'Enregistrer les sites',
    saving: 'Enregistrement…',
    copyCode: 'Copier le code',
    noSites:
      'Ajoutez d’abord un site : l’application ne peut être intégrée que sur les sites de votre liste.',
    unpublish: 'Dépublier',
    unpublishBody: 'Le lien cesse de fonctionner immédiatement et sa copie est supprimée.',
    unpublishConfirm: 'Dépublier maintenant',
    unpublishing: 'Dépublication…',
    cancel: 'Annuler',
    takenDown: 'Poneva a retiré ce lien public. Contactez le support pour le rétablir.',
    takenDownReason: 'Motif : {reason}',
    viewerLive: 'Toute personne disposant de ce lien peut ouvrir une copie en lecture seule.',
    adminLive:
      'Toute personne disposant de ce lien peut ouvrir une copie en lecture seule. En tant qu’admin de l’entreprise, vous pouvez le retirer.',
    failed: 'Nous n’avons pas pu modifier le lien public. Réessayez.',
    sitesInvalid: 'Saisissez chaque site sous la forme https:// suivi de son adresse, sans chemin.',
    copyTooLarge:
      'Une table est trop volumineuse pour une copie publique. Réduisez-la avec un filtre dans le chat de l’application.',
    rebuild: 'Enregistrez à nouveau l’application dans son chat, puis publiez-la.',
    loadFailed: 'Le lien public n’a pas pu être lu.',
    retry: 'Réessayer',
    checking:
      'Nous vérifions cette version à la recherche de clés divulguées et de paquets non sûrs. Réessayez dans un instant.',
    checkFailed: 'Le contrôle de sécurité n’a pas pu se terminer. Réessayez dans un instant.',
    checkBlockedSecret:
      'Cette version contient ce qui ressemble à une clé secrète : elle ne peut pas être rendue publique. Supprimez-la dans le chat de l’application et enregistrez à nouveau.',
    checkBlockedDependency:
      'Cette version utilise un paquet présentant une faille de sécurité connue : elle ne peut pas être rendue publique. Mettez-le à jour dans le chat de l’application et enregistrez à nouveau.',
    versionChanged: 'Une version plus récente a été enregistrée. Ouvrez-la et publiez à nouveau.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  de: {
    title: 'Öffentlicher Link',
    help: 'Alle mit dem Link können eine schreibgeschützte Kopie dieser App ohne Anmeldung öffnen.',
    whatTitle: 'Was öffentlich wird',
    pages: 'Seiten und Design dieser gespeicherten Version',
    copyOf: 'Eine Kopie von {table}: {fields}',
    copyFiltered: 'Eine Kopie der Zeilen von {table}, die zum Filter passen: {fields}',
    sends: 'Besucher können {action} an {table} senden',
    sendsRuns: 'Jede Übermittlung startet {functions}.',
    sendsRunsOff: '{functions} würde nach dem Einschalten ebenfalls starten.',
    never:
      'Nie öffentlich: die anderen Tabellen der App, Ihre Verbindungen, Geheimnisse und Serverfunktionen.',
    nothingDeclared:
      'Die App zeigt noch keine gespeicherten Daten öffentlich. Um festzulegen, was Besucher sehen oder senden, fragen Sie im Chat der App.',
    mark: 'Die Seite zeigt in einer Ecke „Made with Poneva“.',
    confirm: 'Mir ist klar, dass alle mit dem Link dies sehen können.',
    publish: 'Unter einem öffentlichen Link veröffentlichen',
    publishing: 'Wird veröffentlicht…',
    republish: 'Diese Version veröffentlichen',
    behind:
      'Der öffentliche Link zeigt eine frühere Version. Veröffentlichen Sie diese, um ihn zu aktualisieren.',
    live: 'Live',
    copyLink: 'Link kopieren',
    copied: 'Kopiert',
    open: 'Öffnen',
    copiedAt: 'Daten kopiert {time}',
    refresh: 'Daten aktualisieren',
    refreshing: 'Wird aktualisiert…',
    embedTitle: 'In Ihre Website einbetten',
    embedHelp:
      'Nennen Sie die Websites, die diese App anzeigen dürfen, z. B. https://shop.example.com, und fügen Sie den Code dann in eine Seite dort ein.',
    embedSites: 'Websites, die sie einbetten dürfen',
    saveSites: 'Websites speichern',
    saving: 'Wird gespeichert…',
    copyCode: 'Code kopieren',
    noSites:
      'Fügen Sie zuerst eine Website hinzu: Die App kann nur auf aufgelisteten Websites eingebettet werden.',
    unpublish: 'Veröffentlichung zurücknehmen',
    unpublishBody: 'Der Link funktioniert sofort nicht mehr und seine Kopie wird gelöscht.',
    unpublishConfirm: 'Jetzt zurücknehmen',
    unpublishing: 'Wird zurückgenommen…',
    cancel: 'Abbrechen',
    takenDown:
      'Poneva hat diesen öffentlichen Link entfernt. Wenden Sie sich an den Support, um ihn wiederherzustellen.',
    takenDownReason: 'Grund: {reason}',
    viewerLive: 'Alle mit diesem Link können eine schreibgeschützte Kopie öffnen.',
    adminLive:
      'Alle mit diesem Link können eine schreibgeschützte Kopie öffnen. Als Unternehmensadmin können Sie ihn entfernen.',
    failed: 'Wir konnten den öffentlichen Link nicht ändern. Versuchen Sie es erneut.',
    sitesInvalid: 'Geben Sie jede Website als https:// und ihre Adresse ein, ohne Pfad.',
    copyTooLarge:
      'Eine Tabelle ist zu groß für eine öffentliche Kopie. Grenzen Sie sie im Chat der App mit einem Filter ein.',
    rebuild: 'Speichern Sie die App im Chat erneut und veröffentlichen Sie sie dann.',
    loadFailed: 'Der öffentliche Link konnte nicht gelesen werden.',
    retry: 'Erneut versuchen',
    checking:
      'Wir prüfen diese Version auf durchgesickerte Schlüssel und unsichere Pakete. Versuchen Sie es gleich noch einmal.',
    checkFailed:
      'Die Sicherheitsprüfung konnte nicht abgeschlossen werden. Versuchen Sie es gleich noch einmal.',
    checkBlockedSecret:
      'Diese Version enthält etwas, das wie ein geheimer Schlüssel aussieht, und kann daher nicht öffentlich gemacht werden. Entfernen Sie ihn im Chat der App und speichern Sie erneut.',
    checkBlockedDependency:
      'Diese Version nutzt ein Paket mit bekannter Sicherheitslücke und kann daher nicht öffentlich gemacht werden. Aktualisieren Sie es im Chat der App und speichern Sie erneut.',
    versionChanged:
      'Eine neuere Version wurde gespeichert. Öffnen Sie sie und veröffentlichen Sie erneut.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  el: {
    title: 'Δημόσιος σύνδεσμος',
    help: 'Όποιος έχει τον σύνδεσμο μπορεί να ανοίξει ένα αντίγραφο αυτής της Εφαρμογής μόνο για ανάγνωση, χωρίς σύνδεση.',
    whatTitle: 'Τι γίνεται δημόσιο',
    pages: 'Οι σελίδες και ο σχεδιασμός αυτής της αποθηκευμένης έκδοσης',
    copyOf: 'Αντίγραφο του πίνακα {table}: {fields}',
    copyFiltered:
      'Αντίγραφο των γραμμών του πίνακα {table} που ταιριάζουν με το φίλτρο του: {fields}',
    sends: 'Οι επισκέπτες μπορούν να στέλνουν {action} στον πίνακα {table}',
    sendsRuns: 'Κάθε υποβολή εκτελεί {functions}.',
    sendsRunsOff: 'Μόλις ενεργοποιηθεί, θα εκτελείται επίσης: {functions}.',
    never:
      'Ποτέ δημόσια: οι άλλοι πίνακες της Εφαρμογής, οι συνδέσεις σας, τα μυστικά και οι λειτουργίες διακομιστή.',
    nothingDeclared:
      'Η Εφαρμογή δεν εμφανίζει ακόμη αποθηκευμένα δεδομένα δημόσια. Για να επιλέξετε τι βλέπουν ή στέλνουν οι επισκέπτες, ρωτήστε στη συνομιλία της Εφαρμογής.',
    mark: 'Η σελίδα εμφανίζει «Made with Poneva» σε μια γωνία.',
    confirm: 'Καταλαβαίνω ότι όποιος έχει τον σύνδεσμο μπορεί να το δει.',
    publish: 'Δημοσίευση σε δημόσιο σύνδεσμο',
    publishing: 'Δημοσίευση…',
    republish: 'Δημοσίευση αυτής της έκδοσης',
    behind: 'Ο δημόσιος σύνδεσμος δείχνει παλαιότερη έκδοση. Δημοσιεύστε αυτήν για να ενημερωθεί.',
    live: 'Ενεργός',
    copyLink: 'Αντιγραφή συνδέσμου',
    copied: 'Αντιγράφηκε',
    open: 'Άνοιγμα',
    copiedAt: 'Τα δεδομένα αντιγράφηκαν {time}',
    refresh: 'Ανανέωση δεδομένων',
    refreshing: 'Ανανέωση…',
    embedTitle: 'Ενσωμάτωση στον ιστότοπό σας',
    embedHelp:
      'Καταχωρίστε τους ιστότοπους που μπορούν να εμφανίζουν αυτή την Εφαρμογή, όπως https://shop.example.com, και επικολλήστε τον κώδικα σε μια σελίδα εκεί.',
    embedSites: 'Ιστότοποι που μπορούν να την ενσωματώσουν',
    saveSites: 'Αποθήκευση ιστότοπων',
    saving: 'Αποθήκευση…',
    copyCode: 'Αντιγραφή κώδικα',
    noSites:
      'Προσθέστε πρώτα έναν ιστότοπο: η Εφαρμογή μπορεί να ενσωματωθεί μόνο σε ιστότοπους που καταχωρίζετε.',
    unpublish: 'Κατάργηση δημοσίευσης',
    unpublishBody: 'Ο σύνδεσμος σταματά να λειτουργεί αμέσως και το αντίγραφό του διαγράφεται.',
    unpublishConfirm: 'Κατάργηση τώρα',
    unpublishing: 'Κατάργηση δημοσίευσης…',
    cancel: 'Ακύρωση',
    takenDown:
      'Η Poneva κατέβασε αυτόν τον δημόσιο σύνδεσμο. Επικοινωνήστε με την υποστήριξη για να τον επαναφέρετε.',
    takenDownReason: 'Αιτία: {reason}',
    viewerLive: 'Όποιος έχει αυτόν τον σύνδεσμο μπορεί να ανοίξει ένα αντίγραφο μόνο για ανάγνωση.',
    adminLive:
      'Όποιος έχει αυτόν τον σύνδεσμο μπορεί να ανοίξει ένα αντίγραφο μόνο για ανάγνωση. Ως διαχειριστής της εταιρείας, μπορείτε να τον κατεβάσετε.',
    failed: 'Δεν μπορέσαμε να αλλάξουμε τον δημόσιο σύνδεσμο. Δοκιμάστε ξανά.',
    sitesInvalid: 'Εισαγάγετε κάθε ιστότοπο ως https:// και τη διεύθυνσή του, χωρίς διαδρομή.',
    copyTooLarge:
      'Ένας πίνακας είναι πολύ μεγάλος για δημόσιο αντίγραφο. Περιορίστε τον με φίλτρο στη συνομιλία της Εφαρμογής.',
    rebuild: 'Αποθηκεύστε ξανά την Εφαρμογή στη συνομιλία της και μετά δημοσιεύστε την.',
    loadFailed: 'Δεν ήταν δυνατή η ανάγνωση του δημόσιου συνδέσμου.',
    retry: 'Δοκιμάστε ξανά',
    checking:
      'Ελέγχουμε αυτή την έκδοση για εκτεθειμένα κλειδιά και μη ασφαλή πακέτα. Δοκιμάστε ξανά σε λίγο.',
    checkFailed: 'Ο έλεγχος ασφαλείας δεν μπόρεσε να ολοκληρωθεί. Δοκιμάστε ξανά σε λίγο.',
    checkBlockedSecret:
      'Αυτή η έκδοση περιέχει κάτι που μοιάζει με μυστικό κλειδί, οπότε δεν μπορεί να γίνει δημόσια. Αφαιρέστε το στη συνομιλία της Εφαρμογής και αποθηκεύστε ξανά.',
    checkBlockedDependency:
      'Αυτή η έκδοση χρησιμοποιεί ένα πακέτο με γνωστό πρόβλημα ασφαλείας, οπότε δεν μπορεί να γίνει δημόσια. Ενημερώστε το στη συνομιλία της Εφαρμογής και αποθηκεύστε ξανά.',
    versionChanged: 'Αποθηκεύτηκε νεότερη έκδοση. Ανοίξτε την και δημοσιεύστε ξανά.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  hu: {
    title: 'Nyilvános hivatkozás',
    help: 'A hivatkozással bárki megnyithatja az alkalmazás csak olvasható másolatát bejelentkezés nélkül.',
    whatTitle: 'Mi válik nyilvánossá',
    pages: 'Ennek a mentett verziónak az oldalai és megjelenése',
    copyOf: 'A(z) {table} tábla másolata: {fields}',
    copyFiltered: 'A(z) {table} tábla szűrőjének megfelelő sorainak másolata: {fields}',
    sends: 'A látogatók küldhetnek {action} ebbe a táblába: {table}',
    sendsRuns: 'Minden beküldés elindítja ezt: {functions}.',
    sendsRunsOff: 'Bekapcsolás után ez is elindul: {functions}.',
    never:
      'Soha nem nyilvános: az alkalmazás többi táblája, a kapcsolataid, a titkos kulcsok és a szerverfüggvények.',
    nothingDeclared:
      'Az alkalmazás még nem mutat nyilvánosan mentett adatokat. Hogy kiválaszd, mit lássanak vagy küldjenek a látogatók, kérd az alkalmazás csevegésében.',
    mark: 'Az oldal egyik sarkában a „Létrehozva: Poneva” felirat látható.',
    confirm: 'Értem, hogy ezt bárki láthatja, aki rendelkezik a hivatkozással.',
    publish: 'Közzététel nyilvános hivatkozással',
    publishing: 'Közzététel…',
    republish: 'Ezen verzió közzététele',
    behind: 'A nyilvános hivatkozás egy korábbi verziót mutat. A frissítéshez tedd közzé ezt.',
    live: 'Közzétéve',
    copyLink: 'Hivatkozás másolása',
    copied: 'Másolva',
    open: 'Megnyitás',
    copiedAt: 'Adatok másolva: {time}',
    refresh: 'Adatok frissítése',
    refreshing: 'Frissítés…',
    embedTitle: 'Beágyazás a weboldaladra',
    embedHelp:
      'Add meg azokat a webhelyeket, amelyek megjeleníthetik az alkalmazást, például https://shop.example.com, majd illeszd be a kódot az egyik oldalára.',
    embedSites: 'Webhelyek, amelyek beágyazhatják',
    saveSites: 'Webhelyek mentése',
    saving: 'Mentés…',
    copyCode: 'Kód másolása',
    noSites:
      'Előbb adj hozzá egy webhelyet: az alkalmazás csak az általad megadott webhelyeken ágyazható be.',
    unpublish: 'Közzététel visszavonása',
    unpublishBody: 'A hivatkozás azonnal megszűnik működni, a másolata pedig törlődik.',
    unpublishConfirm: 'Visszavonás most',
    unpublishing: 'Visszavonás…',
    cancel: 'Mégse',
    takenDown:
      'A Poneva levette ezt a nyilvános hivatkozást. A visszaállításhoz fordulj az ügyfélszolgálathoz.',
    takenDownReason: 'Ok: {reason}',
    viewerLive: 'Ezzel a hivatkozással bárki megnyithatja a csak olvasható másolatot.',
    adminLive:
      'Ezzel a hivatkozással bárki megnyithatja a csak olvasható másolatot. Vállalati rendszergazdaként leveheted.',
    failed: 'A nyilvános hivatkozást nem sikerült módosítani. Próbáld újra.',
    sitesInvalid: 'Minden webhelyet https:// előtaggal és a címével add meg, elérési út nélkül.',
    copyTooLarge:
      'Egy tábla túl nagy a nyilvános másoláshoz. Szűkítsd szűrővel az alkalmazás csevegésében.',
    rebuild: 'Mentsd el újra az alkalmazást a csevegésében, majd tedd közzé.',
    loadFailed: 'A nyilvános hivatkozást nem sikerült beolvasni.',
    retry: 'Próbáld újra',
    checking:
      'Ellenőrizzük, hogy ez a verzió tartalmaz-e kiszivárgott kulcsokat vagy nem biztonságos csomagokat. Próbáld újra egy pillanat múlva.',
    checkFailed:
      'A biztonsági ellenőrzést nem sikerült befejezni. Próbáld újra egy pillanat múlva.',
    checkBlockedSecret:
      'Ez a verzió titkos kulcsnak tűnő adatot tartalmaz, ezért nem tehető nyilvánossá. Távolítsd el az alkalmazás csevegésében, és mentsd újra.',
    checkBlockedDependency:
      'Ez a verzió ismert biztonsági hibát tartalmazó csomagot használ, ezért nem tehető nyilvánossá. Frissítsd az alkalmazás csevegésében, és mentsd újra.',
    versionChanged: 'Újabb verzió lett elmentve. Nyisd meg, és tedd közzé újra.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  ga: {
    title: 'Nasc poiblí',
    help: 'Is féidir le duine ar bith a bhfuil an nasc aige cóip léite amháin den aip seo a oscailt, gan síniú isteach.',
    whatTitle: 'Cad a thagann poiblí',
    pages: 'Leathanaigh agus dearadh an leagain shábháilte seo',
    copyOf: 'Cóip den tábla {table}: {fields}',
    copyFiltered: 'Cóip de na sraitheanna sa tábla {table} a mheaitseálann a scagaire: {fields}',
    sends: 'Is féidir le cuairteoirí {action} a sheoladh chuig an tábla {table}',
    sendsRuns: 'Cuireann gach aighneacht {functions} ar siúl.',
    sendsRunsOff: 'Nuair a chuirtear air é, cuirfear {functions} ar siúl freisin.',
    never:
      'Ní bhíonn poiblí choíche: táblaí eile na haipe, do naisc, rúin agus feidhmeanna freastalaí.',
    nothingDeclared:
      'Níl aon sonraí sábháilte á dtaispeáint go poiblí ag an aip fós. Chun a roghnú cad a fheiceann nó a sheolann cuairteoirí, fiafraigh i gcomhrá na haipe.',
    mark: 'Taispeánann an leathanach “Made with Poneva” i gcúinne.',
    confirm: 'Tuigim gur féidir le duine ar bith a bhfuil an nasc aige é seo a fheiceáil.',
    publish: 'Foilsigh chuig nasc poiblí',
    publishing: 'Á fhoilsiú…',
    republish: 'Foilsigh an leagan seo',
    behind:
      'Taispeánann an nasc poiblí leagan níos luaithe. Foilsigh an ceann seo chun é a nuashonrú.',
    live: 'Beo',
    copyLink: 'Cóipeáil an nasc',
    copied: 'Cóipeáilte',
    open: 'Oscail',
    copiedAt: 'Cóipeáladh na sonraí {time}',
    refresh: 'Athnuaigh na sonraí',
    refreshing: 'Á athnuachan…',
    embedTitle: 'Leabaigh ar do shuíomh gréasáin',
    embedHelp:
      'Liostaigh na suíomhanna ar féidir leo an aip seo a thaispeáint, mar shampla https://shop.example.com, ansin greamaigh an cód ar leathanach ansin.',
    embedSites: 'Suíomhanna ar féidir leo í a leabú',
    saveSites: 'Sábháil suíomhanna',
    saving: 'Á shábháil…',
    copyCode: 'Cóipeáil an cód',
    noSites:
      'Cuir suíomh leis ar dtús: ní féidir an aip a leabú ach ar suíomhanna a liostaíonn tú.',
    unpublish: 'Dífhoilsigh',
    unpublishBody: 'Stopann an nasc ag obair láithreach agus scriostar a chóip.',
    unpublishConfirm: 'Dífhoilsigh anois',
    unpublishing: 'Á dhífhoilsiú…',
    cancel: 'Cealaigh',
    takenDown:
      'Bhain Poneva an nasc poiblí seo anuas. Déan teagmháil leis an tacaíocht chun é a athchóiriú.',
    takenDownReason: 'Cúis: {reason}',
    viewerLive: 'Is féidir le duine ar bith a bhfuil an nasc seo aige cóip léite amháin a oscailt.',
    adminLive:
      'Is féidir le duine ar bith a bhfuil an nasc seo aige cóip léite amháin a oscailt. Mar riarthóir cuideachta, is féidir leat é a bhaint anuas.',
    failed: 'Ní raibh muid in ann an nasc poiblí a athrú. Bain triail eile as.',
    sitesInvalid: 'Cuir isteach gach suíomh mar https:// agus a sheoladh, gan cosán.',
    copyTooLarge:
      'Tá tábla ró-mhór le cóipeáil go poiblí. Cúngaigh é le scagaire i gcomhrá na haipe.',
    rebuild: 'Sábháil an aip arís ina comhrá, ansin foilsigh í.',
    loadFailed: 'Níorbh fhéidir an nasc poiblí a léamh.',
    retry: 'Bain triail eile as',
    checking:
      'Táimid ag seiceáil an leagain seo le haghaidh eochracha sceite agus pacáistí neamhshábháilte. Bain triail eile as ar ball beag.',
    checkFailed:
      'Níorbh fhéidir an seiceáil sábháilteachta a chríochnú. Bain triail eile as ar ball beag.',
    checkBlockedSecret:
      'Tá rud éigin sa leagan seo a fhéachann cosúil le heochair rúnda, mar sin ní féidir é a dhéanamh poiblí. Bain é i gcomhrá na haipe agus sábháil arís.',
    checkBlockedDependency:
      'Úsáideann an leagan seo pacáiste a bhfuil fadhb shlándála aitheanta ann, mar sin ní féidir é a dhéanamh poiblí. Nuashonraigh é i gcomhrá na haipe agus sábháil arís.',
    versionChanged: 'Sábháladh leagan níos nuaí. Oscail é agus foilsigh arís.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  it: {
    title: 'Link pubblico',
    help: 'Chiunque abbia il link può aprire una copia di sola lettura di questa app, senza accedere.',
    whatTitle: 'Cosa diventa pubblico',
    pages: 'Le pagine e il design di questa versione salvata',
    copyOf: 'Una copia di {table}: {fields}',
    copyFiltered: 'Una copia delle righe di {table} che corrispondono al filtro: {fields}',
    sends: 'I visitatori possono inviare {action} a {table}',
    sendsRuns: 'Ogni invio avvia {functions}.',
    sendsRunsOff: 'Anche {functions} verrebbe avviata una volta attivata.',
    never:
      'Mai pubblici: le altre tabelle dell’app, le tue connessioni, i segreti e le funzioni server.',
    nothingDeclared:
      'L’app non mostra ancora dati salvati in pubblico. Per scegliere cosa vedono o inviano i visitatori, chiedi nella chat dell’app.',
    mark: 'La pagina mostra “Made with Poneva” in un angolo.',
    confirm: 'Capisco che chiunque abbia il link può vederlo.',
    publish: 'Pubblica su un link pubblico',
    publishing: 'Pubblicazione…',
    republish: 'Pubblica questa versione',
    behind: 'Il link pubblico mostra una versione precedente. Pubblica questa per aggiornarlo.',
    live: 'Attivo',
    copyLink: 'Copia link',
    copied: 'Copiato',
    open: 'Apri',
    copiedAt: 'Dati copiati {time}',
    refresh: 'Aggiorna dati',
    refreshing: 'Aggiornamento…',
    embedTitle: 'Incorpora nel tuo sito web',
    embedHelp:
      'Elenca i siti che possono mostrare questa app, ad esempio https://shop.example.com, poi incolla il codice in una loro pagina.',
    embedSites: 'Siti che possono incorporarla',
    saveSites: 'Salva siti',
    saving: 'Salvataggio…',
    copyCode: 'Copia codice',
    noSites: 'Aggiungi prima un sito: l’app può essere incorporata solo nei siti che elenchi.',
    unpublish: 'Annulla pubblicazione',
    unpublishBody: 'Il link smette di funzionare subito e la sua copia viene eliminata.',
    unpublishConfirm: 'Annulla ora',
    unpublishing: 'Annullamento…',
    cancel: 'Annulla',
    takenDown: 'Poneva ha rimosso questo link pubblico. Contatta l’assistenza per ripristinarlo.',
    takenDownReason: 'Motivo: {reason}',
    viewerLive: 'Chiunque abbia questo link può aprire una copia di sola lettura.',
    adminLive:
      'Chiunque abbia questo link può aprire una copia di sola lettura. Come amministratore dell’azienda, puoi rimuoverlo.',
    failed: 'Non è stato possibile modificare il link pubblico. Riprova.',
    sitesInvalid: 'Inserisci ogni sito come https:// e il suo indirizzo, senza percorso.',
    copyTooLarge:
      'Una tabella è troppo grande per essere copiata in pubblico. Restringila con un filtro nella chat dell’app.',
    rebuild: 'Salva di nuovo l’app nella sua chat, poi pubblicala.',
    loadFailed: 'Non è stato possibile leggere il link pubblico.',
    retry: 'Riprova',
    checking:
      'Stiamo controllando questa versione per chiavi esposte e pacchetti non sicuri. Riprova tra poco.',
    checkFailed: 'Il controllo di sicurezza non è stato completato. Riprova tra poco.',
    checkBlockedSecret:
      'Questa versione contiene quella che sembra una chiave segreta, quindi non può essere resa pubblica. Rimuovila nella chat dell’app e salva di nuovo.',
    checkBlockedDependency:
      'Questa versione usa un pacchetto con un problema di sicurezza noto, quindi non può essere resa pubblica. Aggiornalo nella chat dell’app e salva di nuovo.',
    versionChanged: 'È stata salvata una versione più recente. Aprila e pubblica di nuovo.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  lv: {
    title: 'Publiska saite',
    help: 'Ikviens, kam ir saite, var atvērt šīs lietotnes kopiju tikai lasīšanai, nepierakstoties.',
    whatTitle: 'Kas kļūst publisks',
    pages: 'Šīs saglabātās versijas lapas un dizains',
    copyOf: 'Tabulas {table} kopija: {fields}',
    copyFiltered: 'Tabulas {table} rindu kopija, kas atbilst tās filtram: {fields}',
    sends: 'Apmeklētāji var sūtīt {action} uz tabulu {table}',
    sendsRuns: 'Katrs iesniegums palaiž {functions}.',
    sendsRunsOff: 'Pēc ieslēgšanas tiks palaisti arī: {functions}.',
    never:
      'Nekad nav publiski: lietotnes citas tabulas, jūsu savienojumi, noslēpumi un servera funkcijas.',
    nothingDeclared:
      'Lietotne vēl nerāda publiski nekādus saglabātos datus. Lai izvēlētos, ko apmeklētāji redz vai sūta, jautājiet lietotnes tērzēšanā.',
    mark: 'Lapas stūrī tiek rādīts “Made with Poneva”.',
    confirm: 'Es saprotu, ka to var redzēt ikviens, kam ir saite.',
    publish: 'Publicēt publiskā saitē',
    publishing: 'Notiek publicēšana…',
    republish: 'Publicēt šo versiju',
    behind: 'Publiskā saite rāda agrāku versiju. Publicējiet šo, lai to atjauninātu.',
    live: 'Tiešsaistē',
    copyLink: 'Kopēt saiti',
    copied: 'Nokopēts',
    open: 'Atvērt',
    copiedAt: 'Dati nokopēti {time}',
    refresh: 'Atjaunināt datus',
    refreshing: 'Notiek atjaunināšana…',
    embedTitle: 'Iegult savā vietnē',
    embedHelp:
      'Uzskaitiet vietnes, kurās drīkst rādīt šo lietotni, piemēram, https://shop.example.com, un ielīmējiet kodu tur kādā lapā.',
    embedSites: 'Vietnes, kurās to drīkst iegult',
    saveSites: 'Saglabāt vietnes',
    saving: 'Notiek saglabāšana…',
    copyCode: 'Kopēt kodu',
    noSites: 'Vispirms pievienojiet vietni: lietotni var iegult tikai jūsu norādītajās vietnēs.',
    unpublish: 'Atcelt publicēšanu',
    unpublishBody: 'Saite uzreiz pārstāj darboties, un tās kopija tiek dzēsta.',
    unpublishConfirm: 'Atcelt tagad',
    unpublishing: 'Notiek publicēšanas atcelšana…',
    cancel: 'Atcelt',
    takenDown: 'Poneva noņēma šo publisko saiti. Lai to atjaunotu, sazinieties ar atbalstu.',
    takenDownReason: 'Iemesls: {reason}',
    viewerLive: 'Ikviens, kam ir šī saite, var atvērt kopiju tikai lasīšanai.',
    adminLive:
      'Ikviens, kam ir šī saite, var atvērt kopiju tikai lasīšanai. Kā uzņēmuma administrators varat to noņemt.',
    failed: 'Neizdevās mainīt publisko saiti. Mēģiniet vēlreiz.',
    sitesInvalid: 'Ievadiet katru vietni kā https:// un tās adresi, bez ceļa.',
    copyTooLarge:
      'Tabula ir pārāk liela, lai to publiski kopētu. Sašauriniet to ar filtru lietotnes tērzēšanā.',
    rebuild: 'Saglabājiet lietotni vēlreiz tās tērzēšanā un pēc tam publicējiet.',
    loadFailed: 'Neizdevās nolasīt publisko saiti.',
    retry: 'Mēģināt vēlreiz',
    checking:
      'Pārbaudām šo versiju, vai tajā nav noplūdušu atslēgu un nedrošu pakotņu. Pēc brīža mēģiniet vēlreiz.',
    checkFailed: 'Drošības pārbaudi neizdevās pabeigt. Pēc brīža mēģiniet vēlreiz.',
    checkBlockedSecret:
      'Šajā versijā ir kaut kas, kas izskatās pēc slepenas atslēgas, tāpēc to nevar padarīt publisku. Noņemiet to lietotnes tērzēšanā un saglabājiet vēlreiz.',
    checkBlockedDependency:
      'Šajā versijā tiek izmantota pakotne ar zināmu drošības problēmu, tāpēc to nevar padarīt publisku. Atjauniniet to lietotnes tērzēšanā un saglabājiet vēlreiz.',
    versionChanged: 'Ir saglabāta jaunāka versija. Atveriet to un publicējiet vēlreiz.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  lt: {
    title: 'Vieša nuoroda',
    help: 'Kiekvienas, turintis nuorodą, gali atidaryti šios programos kopiją tik skaitymui, neprisijungęs.',
    whatTitle: 'Kas tampa viešas',
    pages: 'Šios išsaugotos versijos puslapiai ir dizainas',
    copyOf: 'Lentelės {table} kopija: {fields}',
    copyFiltered: 'Lentelės {table} eilučių, atitinkančių jos filtrą, kopija: {fields}',
    sends: 'Lankytojai gali siųsti {action} į lentelę {table}',
    sendsRuns: 'Kiekvienas pateikimas paleidžia {functions}.',
    sendsRunsOff: 'Įjungus bus paleista ir: {functions}.',
    never:
      'Niekada nebūna viešos: kitos programos lentelės, jūsų ryšiai, paslaptys ir serverio funkcijos.',
    nothingDeclared:
      'Programa dar viešai nerodo jokių išsaugotų duomenų. Norėdami pasirinkti, ką lankytojai mato ar siunčia, paklauskite programos pokalbyje.',
    mark: 'Puslapio kampe rodoma „Made with Poneva“.',
    confirm: 'Suprantu, kad tai gali matyti kiekvienas, turintis nuorodą.',
    publish: 'Paskelbti viešoje nuorodoje',
    publishing: 'Skelbiama…',
    republish: 'Paskelbti šią versiją',
    behind: 'Viešoji nuoroda rodo ankstesnę versiją. Paskelbkite šią, kad ją atnaujintumėte.',
    live: 'Veikia',
    copyLink: 'Kopijuoti nuorodą',
    copied: 'Nukopijuota',
    open: 'Atidaryti',
    copiedAt: 'Duomenys nukopijuoti {time}',
    refresh: 'Atnaujinti duomenis',
    refreshing: 'Atnaujinama…',
    embedTitle: 'Įterpti savo svetainėje',
    embedHelp:
      'Išvardykite svetaines, kuriose leidžiama rodyti šią programą, pvz., https://shop.example.com, tada įklijuokite kodą jų puslapyje.',
    embedSites: 'Svetainės, kuriose galima įterpti',
    saveSites: 'Išsaugoti svetaines',
    saving: 'Išsaugoma…',
    copyCode: 'Kopijuoti kodą',
    noSites:
      'Pirmiausia pridėkite svetainę: programą galima įterpti tik jūsų nurodytose svetainėse.',
    unpublish: 'Nebeskelbti',
    unpublishBody: 'Nuoroda iškart nustoja veikti, o jos kopija ištrinama.',
    unpublishConfirm: 'Nebeskelbti dabar',
    unpublishing: 'Skelbimas nutraukiamas…',
    cancel: 'Atšaukti',
    takenDown:
      'Poneva pašalino šią viešą nuorodą. Susisiekite su palaikymo komanda, kad ją atkurtų.',
    takenDownReason: 'Priežastis: {reason}',
    viewerLive: 'Kiekvienas, turintis šią nuorodą, gali atidaryti kopiją tik skaitymui.',
    adminLive:
      'Kiekvienas, turintis šią nuorodą, gali atidaryti kopiją tik skaitymui. Kaip įmonės administratorius galite ją pašalinti.',
    failed: 'Nepavyko pakeisti viešos nuorodos. Bandykite dar kartą.',
    sitesInvalid: 'Įveskite kiekvieną svetainę kaip https:// ir jos adresą, be kelio.',
    copyTooLarge:
      'Lentelė per didelė, kad ją būtų galima viešai kopijuoti. Susiaurinkite ją filtru programos pokalbyje.',
    rebuild: 'Dar kartą išsaugokite programą jos pokalbyje, tada paskelbkite.',
    loadFailed: 'Nepavyko perskaityti viešos nuorodos.',
    retry: 'Bandyti dar kartą',
    checking:
      'Tikriname šią versiją, ar joje nėra nutekėjusių raktų ir nesaugių paketų. Po akimirkos bandykite dar kartą.',
    checkFailed: 'Saugos patikros nepavyko užbaigti. Po akimirkos bandykite dar kartą.',
    checkBlockedSecret:
      'Šioje versijoje yra kažkas, kas atrodo kaip slaptas raktas, todėl jos negalima padaryti viešos. Pašalinkite jį programos pokalbyje ir išsaugokite iš naujo.',
    checkBlockedDependency:
      'Šioje versijoje naudojamas paketas su žinoma saugos problema, todėl jos negalima padaryti viešos. Atnaujinkite jį programos pokalbyje ir išsaugokite iš naujo.',
    versionChanged: 'Išsaugota naujesnė versija. Atidarykite ją ir paskelbkite dar kartą.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  mt: {
    title: 'Link pubbliku',
    help: 'Kull min għandu l-link jista’ jiftaħ kopja għall-qari biss ta’ din l-app, mingħajr ma jidħol.',
    whatTitle: 'X’isir pubbliku',
    pages: 'Il-paġni u d-disinn ta’ din il-verżjoni miżmuma',
    copyOf: 'Kopja ta’ {table}: {fields}',
    copyFiltered: 'Kopja tar-ringieli ta’ {table} li jaqblu mal-filtru: {fields}',
    sends: 'Il-viżitaturi jistgħu jibagħtu {action} lil {table}',
    sendsRuns: 'Kull sottomissjoni tibda {functions}.',
    sendsRunsOff: '{functions} tibda wkoll ladarba tiġi attivata.',
    never:
      'Qatt pubbliku: it-tabelli l-oħra tal-app, il-konnessjonijiet tiegħek, is-sigrieti u l-funzjonijiet tas-server.',
    nothingDeclared:
      'L-app għadha ma turi l-ebda dejta miżmuma pubblikament. Biex tagħżel x’jaraw jew jibagħtu l-viżitaturi, staqsi fiċ-chat tal-app.',
    mark: 'Il-paġna turi “Made with Poneva” f’rokna.',
    confirm: 'Nifhem li kull min għandu l-link jista’ jara dan.',
    publish: 'Ippubblika f’link pubbliku',
    publishing: 'Qed jiġi ppubblikat…',
    republish: 'Ippubblika din il-verżjoni',
    behind: 'Il-link pubbliku juri verżjoni eqdem. Ippubblika din biex taġġornah.',
    live: 'Attiv',
    copyLink: 'Ikkopja l-link',
    copied: 'Ikkupjat',
    open: 'Iftaħ',
    copiedAt: 'Dejta kkupjata {time}',
    refresh: 'Aġġorna d-dejta',
    refreshing: 'Qed jaġġorna…',
    embedTitle: 'Daħħal fis-sit tiegħek',
    embedHelp:
      'Elenka s-siti li jistgħu juru din l-app, bħal https://shop.example.com, imbagħad waħħal il-kodiċi f’paġna tagħhom.',
    embedSites: 'Siti li jistgħu jdaħħluha',
    saveSites: 'Aħżen is-siti',
    saving: 'Qed jinħaras…',
    copyCode: 'Ikkopja l-kodiċi',
    noSites: 'Żid sit l-ewwel: l-app tista’ ttħaddem biss fis-siti li telenka.',
    unpublish: 'Neħħi l-pubblikazzjoni',
    unpublishBody: 'Il-link jieqaf jaħdem minnufih u l-kopja tiegħu titħassar.',
    unpublishConfirm: 'Neħħi issa',
    unpublishing: 'Qed titneħħa l-pubblikazzjoni…',
    cancel: 'Ikkanċella',
    takenDown: 'Poneva neħħiet dan il-link pubbliku. Ikkuntattja s-support biex jerġa’ jitreġġa’.',
    takenDownReason: 'Raġuni: {reason}',
    viewerLive: 'Kull min għandu dan il-link jista’ jiftaħ kopja għall-qari biss.',
    adminLive:
      'Kull min għandu dan il-link jista’ jiftaħ kopja għall-qari biss. Bħala amministratur tal-kumpanija, tista’ tneħħih.',
    failed: 'Ma stajniex nibdlu l-link pubbliku. Erġa’ pprova.',
    sitesInvalid: 'Daħħal kull sit bħala https:// u l-indirizz tiegħu, mingħajr mogħdija.',
    copyTooLarge:
      'Tabella hija kbira wisq biex tiġi kkupjata pubblikament. Ċekknha b’filtru fiċ-chat tal-app.',
    rebuild: 'Aħżen l-app mill-ġdid fiċ-chat tagħha, imbagħad ippubblikaha.',
    loadFailed: 'Il-link pubbliku ma setax jinqara.',
    retry: 'Erġa’ pprova',
    checking:
      'Qed niċċekkjaw din il-verżjoni għal ċwievet li ħarġu u pakketti mhux sikuri. Erġa’ pprova wara ftit.',
    checkFailed: 'Il-verifika tas-sigurtà ma setgħetx titlesta. Erġa’ pprova wara ftit.',
    checkBlockedSecret:
      'Din il-verżjoni fiha dak li jidher bħala ċavetta sigrieta, għalhekk ma tistax issir pubblika. Neħħiha fiċ-chat tal-app u aħżen mill-ġdid.',
    checkBlockedDependency:
      'Din il-verżjoni tuża pakkett b’problema ta’ sigurtà magħrufa, għalhekk ma tistax issir pubblika. Aġġornah fiċ-chat tal-app u aħżen mill-ġdid.',
    versionChanged: 'Inħażnet verżjoni aktar riċenti. Iftaħha u ippubblika mill-ġdid.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  pl: {
    title: 'Link publiczny',
    help: 'Każdy, kto ma link, może otworzyć kopię tej aplikacji tylko do odczytu, bez logowania.',
    whatTitle: 'Co staje się publiczne',
    pages: 'Strony i projekt tej zapisanej wersji',
    copyOf: 'Kopia tabeli {table}: {fields}',
    copyFiltered: 'Kopia wierszy tabeli {table} pasujących do filtra: {fields}',
    sends: 'Odwiedzający mogą wysyłać {action} do tabeli {table}',
    sendsRuns: 'Każde zgłoszenie uruchamia: {functions}.',
    sendsRunsOff: 'Po włączeniu uruchomią się też: {functions}.',
    never:
      'Nigdy publiczne: pozostałe tabele aplikacji, Twoje połączenia, sekrety i funkcje serwerowe.',
    nothingDeclared:
      'Aplikacja nie pokazuje jeszcze publicznie żadnych zapisanych danych. Aby wybrać, co odwiedzający widzą lub wysyłają, zapytaj na czacie aplikacji.',
    mark: 'Strona pokazuje w rogu napis „Utworzone w Poneva”.',
    confirm: 'Rozumiem, że każdy, kto ma link, może to zobaczyć.',
    publish: 'Opublikuj pod linkiem publicznym',
    publishing: 'Publikowanie…',
    republish: 'Opublikuj tę wersję',
    behind: 'Link publiczny pokazuje wcześniejszą wersję. Opublikuj tę, aby go zaktualizować.',
    live: 'Opublikowano',
    copyLink: 'Kopiuj link',
    copied: 'Skopiowano',
    open: 'Otwórz',
    copiedAt: 'Dane skopiowano {time}',
    refresh: 'Odśwież dane',
    refreshing: 'Odświeżanie…',
    embedTitle: 'Osadź na swojej stronie',
    embedHelp:
      'Podaj witryny, które mogą wyświetlać tę aplikację, np. https://shop.example.com, a następnie wklej kod na stronie w jednej z nich.',
    embedSites: 'Witryny, które mogą ją osadzić',
    saveSites: 'Zapisz witryny',
    saving: 'Zapisywanie…',
    copyCode: 'Kopiuj kod',
    noSites: 'Najpierw dodaj witrynę: aplikację można osadzić tylko w podanych witrynach.',
    unpublish: 'Cofnij publikację',
    unpublishBody: 'Link przestaje działać od razu, a jego kopia zostaje usunięta.',
    unpublishConfirm: 'Cofnij publikację teraz',
    unpublishing: 'Cofanie publikacji…',
    cancel: 'Anuluj',
    takenDown:
      'Poneva wyłączyła ten link publiczny. Skontaktuj się z pomocą techniczną, aby go przywrócić.',
    takenDownReason: 'Powód: {reason}',
    viewerLive: 'Każdy, kto ma ten link, może otworzyć kopię tylko do odczytu.',
    adminLive:
      'Każdy, kto ma ten link, może otworzyć kopię tylko do odczytu. Jako administrator firmy możesz go wyłączyć.',
    failed: 'Nie udało się zmienić linku publicznego. Spróbuj ponownie.',
    sitesInvalid: 'Wpisz każdą witrynę jako https:// i jej adres, bez ścieżki.',
    copyTooLarge:
      'Tabela jest zbyt duża, aby skopiować ją publicznie. Zawęź ją filtrem na czacie aplikacji.',
    rebuild: 'Zapisz aplikację ponownie na jej czacie, a potem ją opublikuj.',
    loadFailed: 'Nie udało się odczytać linku publicznego.',
    retry: 'Spróbuj ponownie',
    checking:
      'Sprawdzamy tę wersję pod kątem wyciekłych kluczy i niebezpiecznych pakietów. Spróbuj ponownie za chwilę.',
    checkFailed: 'Nie udało się ukończyć kontroli bezpieczeństwa. Spróbuj ponownie za chwilę.',
    checkBlockedSecret:
      'Ta wersja zawiera coś, co wygląda na klucz tajny, więc nie może zostać upubliczniona. Usuń go na czacie aplikacji i zapisz ponownie.',
    checkBlockedDependency:
      'Ta wersja używa pakietu ze znaną luką w zabezpieczeniach, więc nie może zostać upubliczniona. Zaktualizuj go na czacie aplikacji i zapisz ponownie.',
    versionChanged: 'Zapisano nowszą wersję. Otwórz ją i opublikuj ponownie.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  pt: {
    title: 'Link público',
    help: 'Qualquer pessoa com o link pode abrir uma cópia somente leitura deste aplicativo, sem fazer login.',
    whatTitle: 'O que se torna público',
    pages: 'As páginas e o design desta versão salva',
    copyOf: 'Uma cópia de {table}: {fields}',
    copyFiltered: 'Uma cópia das linhas de {table} que correspondem ao filtro: {fields}',
    sends: 'Os visitantes podem enviar {action} para {table}',
    sendsRuns: 'Cada envio inicia {functions}.',
    sendsRunsOff: '{functions} também seria iniciada depois de ativada.',
    never:
      'Nunca é público: as outras tabelas do aplicativo, suas conexões, segredos e funções de servidor.',
    nothingDeclared:
      'O aplicativo ainda não mostra dados salvos publicamente. Para escolher o que os visitantes veem ou enviam, peça no chat do aplicativo.',
    mark: 'A página mostra “Made with Poneva” em um canto.',
    confirm: 'Entendo que qualquer pessoa com o link pode ver isto.',
    publish: 'Publicar em um link público',
    publishing: 'Publicando…',
    republish: 'Publicar esta versão',
    behind: 'O link público mostra uma versão anterior. Publique esta para atualizá-lo.',
    live: 'Ativo',
    copyLink: 'Copiar link',
    copied: 'Copiado',
    open: 'Abrir',
    copiedAt: 'Dados copiados {time}',
    refresh: 'Atualizar dados',
    refreshing: 'Atualizando…',
    embedTitle: 'Incorporar no seu site',
    embedHelp:
      'Liste os sites que podem mostrar este aplicativo, como https://shop.example.com, e cole o código em uma página deles.',
    embedSites: 'Sites que podem incorporá-lo',
    saveSites: 'Salvar sites',
    saving: 'Salvando…',
    copyCode: 'Copiar código',
    noSites:
      'Adicione um site primeiro: o aplicativo só pode ser incorporado nos sites que você listar.',
    unpublish: 'Cancelar publicação',
    unpublishBody: 'O link deixa de funcionar imediatamente e a cópia dele é excluída.',
    unpublishConfirm: 'Cancelar agora',
    unpublishing: 'Cancelando publicação…',
    cancel: 'Cancelar',
    takenDown:
      'A Poneva removeu este link público. Entre em contato com o suporte para restaurá-lo.',
    takenDownReason: 'Motivo: {reason}',
    viewerLive: 'Qualquer pessoa com este link pode abrir uma cópia somente leitura.',
    adminLive:
      'Qualquer pessoa com este link pode abrir uma cópia somente leitura. Como administrador da empresa, você pode removê-lo.',
    failed: 'Não foi possível alterar o link público. Tente novamente.',
    sitesInvalid: 'Informe cada site como https:// e o endereço, sem caminho.',
    copyTooLarge:
      'Uma tabela é grande demais para ser copiada publicamente. Restrinja-a com um filtro no chat do aplicativo.',
    rebuild: 'Salve o aplicativo novamente no chat dele e depois publique.',
    loadFailed: 'Não foi possível ler o link público.',
    retry: 'Tentar novamente',
    checking:
      'Estamos verificando esta versão quanto a chaves vazadas e pacotes inseguros. Tente novamente em instantes.',
    checkFailed:
      'Não foi possível concluir a verificação de segurança. Tente novamente em instantes.',
    checkBlockedSecret:
      'Esta versão contém o que parece ser uma chave secreta, então não pode ser tornada pública. Remova-a no chat do aplicativo e salve novamente.',
    checkBlockedDependency:
      'Esta versão usa um pacote com um problema de segurança conhecido, então não pode ser tornada pública. Atualize-o no chat do aplicativo e salve novamente.',
    versionChanged: 'Uma versão mais recente foi salva. Abra-a e publique novamente.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  ro: {
    title: 'Link public',
    help: 'Oricine are linkul poate deschide o copie doar pentru citire a acestei aplicații, fără autentificare.',
    whatTitle: 'Ce devine public',
    pages: 'Paginile și designul acestei versiuni salvate',
    copyOf: 'O copie a {table}: {fields}',
    copyFiltered: 'O copie a rândurilor din {table} care se potrivesc filtrului: {fields}',
    sends: 'Vizitatorii pot trimite {action} către {table}',
    sendsRuns: 'Fiecare trimitere pornește {functions}.',
    sendsRunsOff: '{functions} ar porni și ea după activare.',
    never:
      'Niciodată publice: celelalte tabele ale aplicației, conexiunile, secretele și funcțiile de server.',
    nothingDeclared:
      'Aplicația nu arată încă public date salvate. Pentru a alege ce văd sau trimit vizitatorii, întreabă în chatul aplicației.',
    mark: 'Pagina afișează „Made with Poneva” într-un colț.',
    confirm: 'Înțeleg că oricine are linkul poate vedea acest lucru.',
    publish: 'Publică la un link public',
    publishing: 'Se publică…',
    republish: 'Publică această versiune',
    behind: 'Linkul public arată o versiune mai veche. Publică-o pe aceasta pentru a-l actualiza.',
    live: 'Activ',
    copyLink: 'Copiază linkul',
    copied: 'Copiat',
    open: 'Deschide',
    copiedAt: 'Date copiate {time}',
    refresh: 'Reîmprospătează datele',
    refreshing: 'Se reîmprospătează…',
    embedTitle: 'Încorporează pe site-ul tău',
    embedHelp:
      'Enumeră site-urile care pot afișa această aplicație, de exemplu https://shop.example.com, apoi lipește codul într-o pagină de acolo.',
    embedSites: 'Site-uri care o pot încorpora',
    saveSites: 'Salvează site-urile',
    saving: 'Se salvează…',
    copyCode: 'Copiază codul',
    noSites:
      'Adaugă mai întâi un site: aplicația poate fi încorporată doar pe site-urile enumerate.',
    unpublish: 'Anulează publicarea',
    unpublishBody: 'Linkul încetează să funcționeze imediat, iar copia lui este ștearsă.',
    unpublishConfirm: 'Anulează acum',
    unpublishing: 'Se anulează publicarea…',
    cancel: 'Anulează',
    takenDown: 'Poneva a retras acest link public. Contactează asistența pentru a-l restabili.',
    takenDownReason: 'Motiv: {reason}',
    viewerLive: 'Oricine are acest link poate deschide o copie doar pentru citire.',
    adminLive:
      'Oricine are acest link poate deschide o copie doar pentru citire. Ca administrator al companiei, îl poți retrage.',
    failed: 'Nu am putut schimba linkul public. Încearcă din nou.',
    sitesInvalid: 'Introdu fiecare site ca https:// și adresa lui, fără cale.',
    copyTooLarge:
      'Un tabel este prea mare pentru a fi copiat public. Restrânge-l cu un filtru în chatul aplicației.',
    rebuild: 'Salvează din nou aplicația în chatul ei, apoi publică-o.',
    loadFailed: 'Linkul public nu a putut fi citit.',
    retry: 'Încearcă din nou',
    checking:
      'Verificăm această versiune pentru chei scurse și pachete nesigure. Încearcă din nou peste puțin timp.',
    checkFailed:
      'Verificarea de siguranță nu a putut fi finalizată. Încearcă din nou peste puțin timp.',
    checkBlockedSecret:
      'Această versiune conține ceea ce pare o cheie secretă, deci nu poate fi făcută publică. Elimină-o în chatul aplicației și salvează din nou.',
    checkBlockedDependency:
      'Această versiune folosește un pachet cu o problemă de securitate cunoscută, deci nu poate fi făcută publică. Actualizează-l în chatul aplicației și salvează din nou.',
    versionChanged: 'A fost salvată o versiune mai nouă. Deschide-o și publică din nou.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  sk: {
    title: 'Verejný odkaz',
    help: 'Ktokoľvek s odkazom môže otvoriť kópiu tejto aplikácie len na čítanie, bez prihlásenia.',
    whatTitle: 'Čo sa stane verejným',
    pages: 'Stránky a návrh tejto uloženej verzie',
    copyOf: 'Kópia tabuľky {table}: {fields}',
    copyFiltered: 'Kópia riadkov tabuľky {table}, ktoré zodpovedajú jej filtru: {fields}',
    sends: 'Návštevníci môžu odosielať {action} do tabuľky {table}',
    sendsRuns: 'Každé odoslanie spustí: {functions}.',
    sendsRunsOff: 'Po zapnutí sa spustí aj: {functions}.',
    never:
      'Nikdy verejné: ostatné tabuľky aplikácie, vaše pripojenia, tajné kľúče a serverové funkcie.',
    nothingDeclared:
      'Aplikácia zatiaľ verejne nezobrazuje žiadne uložené údaje. Ak chcete zvoliť, čo návštevníci uvidia alebo odošlú, požiadajte o to v chate aplikácie.',
    mark: 'Stránka v rohu zobrazuje „Vytvorené v Poneva“.',
    confirm: 'Rozumiem, že to môže vidieť ktokoľvek s odkazom.',
    publish: 'Publikovať na verejný odkaz',
    publishing: 'Publikuje sa…',
    republish: 'Publikovať túto verziu',
    behind: 'Verejný odkaz zobrazuje staršiu verziu. Publikujte túto, aby ste ho aktualizovali.',
    live: 'Publikované',
    copyLink: 'Kopírovať odkaz',
    copied: 'Skopírované',
    open: 'Otvoriť',
    copiedAt: 'Údaje skopírované {time}',
    refresh: 'Obnoviť údaje',
    refreshing: 'Obnovuje sa…',
    embedTitle: 'Vložiť na váš web',
    embedHelp:
      'Uveďte weby, ktoré smú túto aplikáciu zobrazovať, napríklad https://shop.example.com, a potom vložte kód na stránku na niektorom z nich.',
    embedSites: 'Weby, ktoré ju smú vložiť',
    saveSites: 'Uložiť weby',
    saving: 'Ukladá sa…',
    copyCode: 'Kopírovať kód',
    noSites: 'Najprv pridajte web: aplikáciu možno vložiť iba na weby, ktoré uvediete.',
    unpublish: 'Zrušiť publikovanie',
    unpublishBody: 'Odkaz okamžite prestane fungovať a jeho kópia sa odstráni.',
    unpublishConfirm: 'Zrušiť publikovanie',
    unpublishing: 'Ruší sa publikovanie…',
    cancel: 'Zrušiť',
    takenDown: 'Poneva tento verejný odkaz odstránila. Na jeho obnovenie kontaktujte podporu.',
    takenDownReason: 'Dôvod: {reason}',
    viewerLive: 'Ktokoľvek s týmto odkazom môže otvoriť kópiu len na čítanie.',
    adminLive:
      'Ktokoľvek s týmto odkazom môže otvoriť kópiu len na čítanie. Ako správca firmy ho môžete odstrániť.',
    failed: 'Verejný odkaz sa nepodarilo zmeniť. Skúste to znova.',
    sitesInvalid: 'Zadajte každý web ako https:// a jeho adresu, bez cesty.',
    copyTooLarge:
      'Tabuľka je príliš veľká na verejné kopírovanie. Zúžte ju filtrom v chate aplikácie.',
    rebuild: 'Uložte aplikáciu znova v jej chate a potom ju publikujte.',
    loadFailed: 'Verejný odkaz sa nepodarilo prečítať.',
    retry: 'Skúsiť znova',
    checking:
      'Kontrolujeme, či táto verzia neobsahuje uniknuté kľúče a nebezpečné balíky. Skúste to o chvíľu znova.',
    checkFailed: 'Bezpečnostnú kontrolu sa nepodarilo dokončiť. Skúste to o chvíľu znova.',
    checkBlockedSecret:
      'Táto verzia obsahuje niečo, čo vyzerá ako tajný kľúč, preto ju nemožno sprístupniť verejne. Odstráňte ho v chate aplikácie a uložte znova.',
    checkBlockedDependency:
      'Táto verzia používa balík so známym bezpečnostným problémom, preto ju nemožno sprístupniť verejne. Aktualizujte ho v chate aplikácie a uložte znova.',
    versionChanged: 'Uložila sa novšia verzia. Otvorte ju a publikujte znova.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  sl: {
    title: 'Javna povezava',
    help: 'Vsak, ki ima povezavo, lahko odpre kopijo te aplikacije samo za branje, brez prijave.',
    whatTitle: 'Kaj postane javno',
    pages: 'Strani in zasnova te shranjene različice',
    copyOf: 'Kopija tabele {table}: {fields}',
    copyFiltered: 'Kopija vrstic tabele {table}, ki ustrezajo njenemu filtru: {fields}',
    sends: 'Obiskovalci lahko pošiljajo {action} v tabelo {table}',
    sendsRuns: 'Vsaka oddaja zažene: {functions}.',
    sendsRunsOff: 'Ko bo vklopljeno, se zažene tudi: {functions}.',
    never:
      'Nikoli javno: druge tabele aplikacije, vaše povezave, skrivni ključi in strežniške funkcije.',
    nothingDeclared:
      'Aplikacija javno še ne prikazuje nobenih shranjenih podatkov. Če želite izbrati, kaj obiskovalci vidijo ali pošljejo, vprašajte v klepetu aplikacije.',
    mark: 'Stran v kotu prikazuje „Ustvarjeno v Poneva“.',
    confirm: 'Razumem, da to lahko vidi vsak, ki ima povezavo.',
    publish: 'Objavi na javni povezavi',
    publishing: 'Objavljanje…',
    republish: 'Objavi to različico',
    behind: 'Javna povezava prikazuje starejšo različico. Objavite to, da jo posodobite.',
    live: 'Objavljeno',
    copyLink: 'Kopiraj povezavo',
    copied: 'Kopirano',
    open: 'Odpri',
    copiedAt: 'Podatki kopirani {time}',
    refresh: 'Osveži podatke',
    refreshing: 'Osveževanje…',
    embedTitle: 'Vdelaj na svoje spletno mesto',
    embedHelp:
      'Navedite spletna mesta, ki smejo prikazovati to aplikacijo, na primer https://shop.example.com, nato kodo prilepite na stran na enem od njih.',
    embedSites: 'Spletna mesta, ki jo smejo vdelati',
    saveSites: 'Shrani spletna mesta',
    saving: 'Shranjevanje…',
    copyCode: 'Kopiraj kodo',
    noSites: 'Najprej dodajte spletno mesto: aplikacijo je mogoče vdelati samo na navedena mesta.',
    unpublish: 'Prekliči objavo',
    unpublishBody: 'Povezava takoj preneha delovati, njena kopija pa se izbriše.',
    unpublishConfirm: 'Prekliči objavo zdaj',
    unpublishing: 'Preklicevanje objave…',
    cancel: 'Prekliči',
    takenDown: 'Poneva je to javno povezavo odstranila. Za obnovitev se obrnite na podporo.',
    takenDownReason: 'Razlog: {reason}',
    viewerLive: 'Vsak, ki ima to povezavo, lahko odpre kopijo samo za branje.',
    adminLive:
      'Vsak, ki ima to povezavo, lahko odpre kopijo samo za branje. Kot skrbnik podjetja jo lahko odstranite.',
    failed: 'Javne povezave ni bilo mogoče spremeniti. Poskusite znova.',
    sitesInvalid: 'Vnesite vsako spletno mesto kot https:// in njegov naslov, brez poti.',
    copyTooLarge:
      'Tabela je prevelika za javno kopiranje. Zožite jo s filtrom v klepetu aplikacije.',
    rebuild: 'Znova shranite aplikacijo v njenem klepetu, nato jo objavite.',
    loadFailed: 'Javne povezave ni bilo mogoče prebrati.',
    retry: 'Poskusi znova',
    checking:
      'Preverjamo, ali ta različica vsebuje pricurljane ključe in nevarne pakete. Poskusite znova čez trenutek.',
    checkFailed: 'Varnostnega preverjanja ni bilo mogoče dokončati. Poskusite znova čez trenutek.',
    checkBlockedSecret:
      'Ta različica vsebuje nekaj, kar je videti kot skrivni ključ, zato je ni mogoče objaviti javno. Odstranite ga v klepetu aplikacije in znova shranite.',
    checkBlockedDependency:
      'Ta različica uporablja paket z znano varnostno težavo, zato je ni mogoče objaviti javno. Posodobite ga v klepetu aplikacije in znova shranite.',
    versionChanged: 'Shranjena je bila novejša različica. Odprite jo in znova objavite.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  es: {
    title: 'Enlace público',
    help: 'Cualquiera con el enlace puede abrir una copia de solo lectura de esta app, sin iniciar sesión.',
    whatTitle: 'Qué se hace público',
    pages: 'Las páginas y el diseño de esta versión guardada',
    copyOf: 'Una copia de {table}: {fields}',
    copyFiltered: 'Una copia de las filas de {table} que cumplen su filtro: {fields}',
    sends: 'Los visitantes pueden enviar {action} a {table}',
    sendsRuns: 'Cada envío inicia {functions}.',
    sendsRunsOff: '{functions} también se iniciaría al activarse.',
    never:
      'Nunca es público: las demás tablas de la app, tus conexiones, secretos y funciones de servidor.',
    nothingDeclared:
      'La app aún no muestra datos guardados públicamente. Para elegir qué ven o envían los visitantes, pregunta en el chat de la app.',
    mark: 'La página muestra “Made with Poneva” en una esquina.',
    confirm: 'Entiendo que cualquiera con el enlace puede ver esto.',
    publish: 'Publicar en un enlace público',
    publishing: 'Publicando…',
    republish: 'Publicar esta versión',
    behind: 'El enlace público muestra una versión anterior. Publica esta para actualizarlo.',
    live: 'Activo',
    copyLink: 'Copiar enlace',
    copied: 'Copiado',
    open: 'Abrir',
    copiedAt: 'Datos copiados {time}',
    refresh: 'Actualizar datos',
    refreshing: 'Actualizando…',
    embedTitle: 'Insertar en tu sitio web',
    embedHelp:
      'Indica los sitios que pueden mostrar esta app, como https://shop.example.com, y pega el código en una página de ellos.',
    embedSites: 'Sitios que pueden insertarla',
    saveSites: 'Guardar sitios',
    saving: 'Guardando…',
    copyCode: 'Copiar código',
    noSites: 'Añade primero un sitio: la app solo se puede insertar en los sitios que indiques.',
    unpublish: 'Anular publicación',
    unpublishBody: 'El enlace deja de funcionar de inmediato y se elimina su copia.',
    unpublishConfirm: 'Anular ahora',
    unpublishing: 'Anulando publicación…',
    cancel: 'Cancelar',
    takenDown: 'Poneva retiró este enlace público. Contacta con soporte para restaurarlo.',
    takenDownReason: 'Motivo: {reason}',
    viewerLive: 'Cualquiera con este enlace puede abrir una copia de solo lectura.',
    adminLive:
      'Cualquiera con este enlace puede abrir una copia de solo lectura. Como administrador de la empresa, puedes retirarlo.',
    failed: 'No pudimos cambiar el enlace público. Inténtalo de nuevo.',
    sitesInvalid: 'Escribe cada sitio como https:// y su dirección, sin ruta.',
    copyTooLarge:
      'Una tabla es demasiado grande para copiarla públicamente. Redúcela con un filtro en el chat de la app.',
    rebuild: 'Guarda la app de nuevo en su chat y luego publícala.',
    loadFailed: 'No se pudo leer el enlace público.',
    retry: 'Inténtalo de nuevo',
    checking:
      'Estamos comprobando esta versión en busca de claves filtradas y paquetes inseguros. Inténtalo de nuevo en un momento.',
    checkFailed:
      'No se pudo completar la comprobación de seguridad. Inténtalo de nuevo en un momento.',
    checkBlockedSecret:
      'Esta versión contiene lo que parece una clave secreta, así que no se puede hacer pública. Elimínala en el chat de la app y guarda de nuevo.',
    checkBlockedDependency:
      'Esta versión usa un paquete con un problema de seguridad conocido, así que no se puede hacer pública. Actualízalo en el chat de la app y guarda de nuevo.',
    versionChanged: 'Se guardó una versión más reciente. Ábrela y publica de nuevo.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
  sv: {
    title: 'Offentlig länk',
    help: 'Alla med länken kan öppna en skrivskyddad kopia av appen, utan att logga in.',
    whatTitle: 'Det här blir offentligt',
    pages: 'Den här sparade versionens sidor och design',
    copyOf: 'En kopia av {table}: {fields}',
    copyFiltered: 'En kopia av de rader i {table} som filtret matchar: {fields}',
    sends: 'Besökare kan skicka {action} till {table}',
    sendsRuns: 'Varje inskick startar {functions}.',
    sendsRunsOff: 'Startar också när de slås på: {functions}.',
    never:
      'Aldrig offentligt: appens andra tabeller, dina anslutningar, hemligheter och serverfunktioner.',
    nothingDeclared:
      'Appen visar inga sparade data offentligt ännu. Be om det i appens chatt för att välja vad besökare ser eller skickar.',
    mark: 'Sidan visar ”Skapad med Poneva” i ett hörn.',
    confirm: 'Jag förstår att alla med länken kan se detta.',
    publish: 'Publicera till en offentlig länk',
    publishing: 'Publicerar…',
    republish: 'Publicera den här versionen',
    behind: 'Den offentliga länken visar en tidigare version. Publicera den här för att uppdatera.',
    live: 'Publicerad',
    copyLink: 'Kopiera länk',
    copied: 'Kopierad',
    open: 'Öppna',
    copiedAt: 'Data kopierade {time}',
    refresh: 'Uppdatera data',
    refreshing: 'Uppdaterar…',
    embedTitle: 'Bädda in på din webbplats',
    embedHelp:
      'Ange webbplatserna som får visa appen, till exempel https://shop.example.com, och klistra sedan in koden på en sida där.',
    embedSites: 'Webbplatser som får bädda in den',
    saveSites: 'Spara webbplatser',
    saving: 'Sparar…',
    copyCode: 'Kopiera kod',
    noSites: 'Lägg till en webbplats först: appen kan bara bäddas in på webbplatser du anger.',
    unpublish: 'Avpublicera',
    unpublishBody: 'Länken slutar fungera direkt och kopian raderas.',
    unpublishConfirm: 'Avpublicera nu',
    unpublishing: 'Avpublicerar…',
    cancel: 'Avbryt',
    takenDown:
      'Poneva har tagit ned den offentliga länken. Kontakta supporten för att återställa den.',
    takenDownReason: 'Skäl: {reason}',
    viewerLive: 'Alla med länken kan öppna en skrivskyddad kopia.',
    adminLive:
      'Alla med länken kan öppna en skrivskyddad kopia. Som administratör i företaget kan du ta ned den.',
    failed: 'Vi kunde inte ändra den offentliga länken. Försök igen.',
    sitesInvalid: 'Ange varje webbplats med https:// och adressen, utan sökväg.',
    copyTooLarge:
      'En tabell är för stor för att kopieras offentligt. Begränsa den med ett filter i appens chatt.',
    rebuild: 'Spara appen igen i dess chatt och publicera den sedan.',
    loadFailed: 'Den offentliga länken kunde inte läsas.',
    retry: 'Försök igen',
    checking:
      'Vi kontrollerar den här versionen efter läckta nycklar och osäkra paket. Försök igen om en stund.',
    checkFailed: 'Säkerhetskontrollen kunde inte slutföras. Försök igen om en stund.',
    checkBlockedSecret:
      'Den här versionen innehåller något som ser ut som en hemlig nyckel och kan inte göras offentlig. Ta bort den i appens chatt och spara igen.',
    checkBlockedDependency:
      'Den här versionen använder ett paket med ett känt säkerhetsproblem och kan inte göras offentlig. Uppdatera det i appens chatt och spara igen.',
    versionChanged: 'En nyare version har sparats. Öppna den och publicera igen.',
    embedSitesPlaceholder: 'https://shop.example.com',
  },
};
export default copy;
